import React, { useState, useMemo } from 'react';
import { 
    Users, CalendarDays, Building2, UserCheck, Calculator, FolderOpen,
    GitMerge, FileSignature, Newspaper, Inbox, Leaf, Megaphone, Settings,
    BarChart3, Home, Folder, Shield, LayoutGrid, Search, Plus, X, 
    CheckCircle, Save, Edit3, Trash2, Copy, ChevronRight, Minus, Loader2, Zap,
    Sun, Moon, Filter, ArrowUp, ArrowDown, ArrowUpDown, Info
} from 'lucide-react';
import { useFirestoreData } from '../../services/firestore';
import { db } from '../../config/firebase'; // necessário para consultas adicionais no mapeamento dinâmico
import { collection, query, limit, getDocs, doc, getDoc, setDoc } from 'firebase/firestore'; // necessário para mapeamento
import { DB_PATH } from '../../config/firebase';
import { 
    MODULES, ICON_MAP, COLOR_PRESETS, PERMISSION_STRUCTURE, DEPT_PERMISSION_MODULES 
} from '../../config/constants';
import { extractJsonPaths, getNestedValue } from '../../utils';
import Button from '../../components/Button';
import Input from '../../components/Input';
import AuditLogTab from './components/AuditLogTab';
import IntegrationDetailsModal from './components/IntegrationDetailsModal';
import InternalTeamTab from './components/InternalTeamTab';
import HeadcountTab from './components/HeadcountTab';
import IntegrationLogsTab from './components/IntegrationLogsTab';

export const AdmModule = ({ 
    isDarkMode, 
    authReady, 
    currentUser, 
    displayModules, 
    logos, 
    saveSetting 
}) => {
    // Hooks de Dados
    const { data: depts, saveItem: saveDept, deleteItem: deleteDept } = useFirestoreData('departments', authReady);
    const { data: mgrs, saveItem: saveManager, deleteItem: deleteManager } = useFirestoreData('managers', authReady);
    const { data: users, saveItem: saveUser, deleteItem: deleteUser } = useFirestoreData('users', authReady);
    const { data: auditLogs, saveItem: saveAuditLog } = useFirestoreData('audit_logs', authReady, 200);
    const { data: permissionProfiles, saveItem: savePermissionProfile, deleteItem: deletePermissionProfile } = useFirestoreData('permissionProfiles', authReady);
    const { data: dbModules, saveItem: saveModule, deleteItem: deleteModule } = useFirestoreData('hub_modules', authReady);
    const { data: integrations, saveItem: saveIntegration, deleteItem: deleteIntegration } = useFirestoreData('hub_integrations', authReady);
    const { data: softwares, saveItem: saveSoftware, deleteItem: deleteSoftware } = useFirestoreData('hub_softwares', authReady);
    const { data: integrationLogs } = useFirestoreData('hub_integration_logs', authReady, 100);
    const { data: permissionCatalog } = useFirestoreData('permission_catalog', authReady);

    // Constrói a estrutura de permissões mesclando cada departamento (Módulo 05) com o catálogo dinâmico do Firestore (permission_catalog)
    const permissionStructure = useMemo(() => {
        const catalogList = Array.isArray(permissionCatalog) ? permissionCatalog : [];

        const sortCatalogItems = (a, b) => {
            const orderA = Number.isFinite(Number(a.order)) ? Number(a.order) : Infinity;
            const orderB = Number.isFinite(Number(b.order)) ? Number(b.order) : Infinity;
            if (orderA !== orderB) return orderA - orderB;

            const sibA = Number.isFinite(Number(a.siblingOrder)) ? Number(a.siblingOrder) : Infinity;
            const sibB = Number.isFinite(Number(b.siblingOrder)) ? Number(b.siblingOrder) : Infinity;
            if (sibA !== sibB) return sibA - sibB;

            if (a.sortPath && b.sortPath && a.sortPath !== b.sortPath) {
                return String(a.sortPath).localeCompare(String(b.sortPath), undefined, { numeric: true });
            }
            return String(a.rawLabel || a.label || a.key || '').localeCompare(String(b.rawLabel || b.label || b.key || ''), 'pt-BR', { numeric: true });
        };

        const buildDeptTree = (deptMod) => {
            const appIdCandidates = (deptMod.appIds || [deptMod.deptCode, deptMod.moduleId])
                .filter(Boolean)
                .map(s => String(s).trim().toLowerCase());

            const deptDocs = catalogList.filter(docItem => {
                if (!docItem || docItem.active === false || !docItem.key) return false;
                const docAppId = String(docItem.appId || '').trim().toLowerCase();
                return appIdCandidates.includes(docAppId);
            });

            if (deptDocs.length === 0) {
                return { ...deptMod, children: [], linkedKeys: [] };
            }

            const nodeMap = new Map();
            deptDocs.forEach(docItem => {
                nodeMap.set(docItem.key, {
                    id: docItem.id || docItem.key,
                    key: docItem.key,
                    rawLabel: docItem.label || docItem.key,
                    label: docItem.label || docItem.key,
                    menuNumber: docItem.menuNumber || docItem.menuCode || null,
                    parentKey: docItem.parentKey || null,
                    defaultAdm: !!docItem.defaultAdm,
                    order: docItem.order,
                    siblingOrder: docItem.siblingOrder,
                    sortPath: docItem.sortPath,
                    children: []
                });
            });

            const rootNodes = [];
            nodeMap.forEach(node => {
                if (node.parentKey && node.parentKey !== node.key && nodeMap.has(node.parentKey)) {
                    nodeMap.get(node.parentKey).children.push(node);
                } else {
                    rootNodes.push(node);
                }
            });

            const sortTreeRecursive = (nodes) => {
                nodes.sort(sortCatalogItems);
                nodes.forEach(n => {
                    if (n.children && n.children.length > 0) {
                        sortTreeRecursive(n.children);
                    }
                });
            };
            sortTreeRecursive(rootNodes);

            let finalChildren = rootNodes;
            const linkedKeys = [];

            // Se houver um nó raiz aglutinador do próprio app (ex: ctb_app_view com label 'CTB'), promove seus filhos como menus diretos do módulo
            if (
                rootNodes.length === 1 &&
                (
                    rootNodes[0].key === deptMod.rootKey ||
                    String(rootNodes[0].key).toLowerCase().endsWith('_app_view') ||
                    String(rootNodes[0].rawLabel || '').trim().toUpperCase() === String(deptMod.deptCode || '').trim().toUpperCase()
                ) &&
                rootNodes[0].children.length > 0
            ) {
                linkedKeys.push(rootNodes[0].key);
                finalChildren = rootNodes[0].children.map(menuNode => {
                    const prefix = menuNode.menuNumber ? String(menuNode.menuNumber).trim() : '';
                    const raw = String(menuNode.rawLabel || menuNode.label || '').trim();
                    const formattedLabel = prefix && !raw.startsWith(prefix) ? `${prefix} - ${raw}` : raw;
                    return {
                        ...menuNode,
                        label: formattedLabel
                    };
                });
            }

            return {
                ...deptMod,
                linkedKeys,
                children: finalChildren
            };
        };

        // Verifica também se existem submódulos extras de '05' cadastrados dinamicamente no banco
        const dynamicExtraDepts = (displayModules || [])
            .filter(m => m.parentId === '05' && !DEPT_PERMISSION_MODULES.some(d => d.moduleId === String(m.id)))
            .map(m => {
                const cleanCode = String(m.title || m.id).trim();
                const keySuffix = String(m.id).replace(/\./g, '_');
                return {
                    id: `mod_${keySuffix}`,
                    moduleId: String(m.id),
                    deptCode: cleanCode.toUpperCase(),
                    appIds: [cleanCode.toLowerCase(), String(m.id).toLowerCase(), `mod_${keySuffix}`.toLowerCase()],
                    rootKey: `${cleanCode.toLowerCase()}_app_view`,
                    label: `Módulo: ${cleanCode} (${m.id})`,
                    key: `access_mod_${keySuffix}`,
                    defaultAdm: true,
                    isDeptModule: true
                };
            });

        const baseStructure = [];
        let deptInserted = false;

        PERMISSION_STRUCTURE.forEach(item => {
            if (item.isDeptModule) {
                baseStructure.push(buildDeptTree(item));
                deptInserted = true;
            } else {
                if (deptInserted && dynamicExtraDepts.length > 0) {
                    dynamicExtraDepts.forEach(extra => baseStructure.push(buildDeptTree(extra)));
                    dynamicExtraDepts.length = 0;
                }
                baseStructure.push(item);
            }
        });

        return baseStructure;
    }, [permissionCatalog, displayModules]);
    
    // Esquemas de Destino Disponíveis (Tabelas e Campos do seu Banco)
    const TARGET_SCHEMAS = {
        clients: {
            label: 'Tabela: Clientes',
            fields: [
                { key: 'id', label: 'ID Único no Sistema Externo *' },
                { key: 'name', label: 'Nome / Razão Social *' },
                { key: 'document', label: 'CPF / CNPJ' },
                { key: 'externalId', label: 'Código Externo (ID Auxiliar)' },
                { key: 'address', label: 'Endereço Completo' },
                { key: 'isActive', label: 'Status (Ativo?)' }
            ]
        },
        acad_colaboradores: {
            label: 'Tabela: Colaboradores Acads (acad_colaboradores)',
            fields: [
                { key: 'id', label: 'ID Único no Sistema Externo *' },
                { key: 'nome', label: 'Nome Completo *' },
                { key: 'matricula', label: 'Matrícula Funcional' },
                { key: 'cpf', label: 'CPF' },
                { key: 'salarioAtual', label: 'Salário Base Atual *' },
                { key: 'cargo', label: 'Cargo / Função' },
                { key: 'dataAdmissao', label: 'Data de Admissão' },
                { key: 'status', label: 'Situação (Ativo/Afastado/Demitido)' },
                { key: 'unidadeNome', label: 'Unidade Acad (Nome/Código)' },
                { key: 'email', label: 'E-mail' },
                { key: 'telefone', label: 'Telefone / Celular' },
                { key: 'chavePix', label: 'Chave PIX' }
            ]
        }
    };

    // Estados do ADM
    const [activeTab, setActiveTab] = useState('dept');
    const [usersSubTab, setUsersSubTab] = useState('list');
    const [integrationsSubTab, setIntegrationsSubTab] = useState('list');
    const [syncVars, setSyncVars] = useState({}); // Motor de Variáveis Dinâmicas
    const [search, setSearch] = useState('');

    // Estados de Ordenação e Filtros de Usuários
    const [userSortField, setUserSortField] = useState('name'); // Padrão: alfabética por Usuário
    const [userSortDirection, setUserSortDirection] = useState('asc'); // 'asc' ou 'desc'
    const [showUserFilters, setShowUserFilters] = useState(false);
    const [filterUserNome, setFilterUserNome] = useState('');
    const [filterUserDept, setFilterUserDept] = useState('');
    const [filterUserTipo, setFilterUserTipo] = useState('');
    const [filterUserStatus, setFilterUserStatus] = useState('');
    const [filterUserConexao, setFilterUserConexao] = useState('');

    const handleUserSort = (field) => {
        if (userSortField === field) {
            setUserSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setUserSortField(field);
            setUserSortDirection('asc');
        }
    };

    const renderSortIcon = (field) => {
        if (userSortField !== field) {
            return <ArrowUpDown size={12} className="opacity-40 group-hover:opacity-100 transition-opacity ml-1 inline shrink-0" />;
        }
        return userSortDirection === 'asc' ? (
            <ArrowUp size={12} className="text-blue-500 ml-1 inline shrink-0" />
        ) : (
            <ArrowDown size={12} className="text-blue-500 ml-1 inline shrink-0" />
        );
    };

    const handleClearUserFilters = () => {
        setFilterUserNome('');
        setFilterUserDept('');
        setFilterUserTipo('');
        setFilterUserStatus('');
        setFilterUserConexao('');
    };

    const activeUserFilterCount = [
        filterUserNome,
        filterUserDept,
        filterUserTipo,
        filterUserStatus,
        filterUserConexao
    ].filter(Boolean).length;

    const availableUserRoles = useMemo(() => {
        const rolesSet = new Set();
        (users || []).forEach(u => {
            if (u.role) rolesSet.add(u.role);
        });
        ['SuperAdm', 'ADM', 'Solicitante'].forEach(r => rolesSet.add(r));
        return Array.from(rolesSet);
    }, [users]);

    const filteredAndSortedUsers = useMemo(() => {
        let list = Array.isArray(users) ? [...users] : [];

        // Busca Geral
        if (search && search.trim()) {
            const s = search.toLowerCase().trim();
            list = list.filter(u => 
                (u.name || '').toLowerCase().includes(s) ||
                (u.email || '').toLowerCase().includes(s)
            );
        }

        // Filtro 1: Usuário (Nome)
        if (filterUserNome && filterUserNome.trim()) {
            const s = filterUserNome.toLowerCase().trim();
            list = list.filter(u => (u.name || '').toLowerCase().includes(s));
        }

        // Filtro 2: Departamento
        if (filterUserDept) {
            list = list.filter(u => String(u.departmentId) === String(filterUserDept));
        }

        // Filtro 3: Tipo (Role)
        if (filterUserTipo) {
            list = list.filter(u => (u.role || '').toLowerCase() === filterUserTipo.toLowerCase());
        }

        // Filtro 4: Status Acesso
        if (filterUserStatus) {
            if (filterUserStatus === 'pendente') {
                list = list.filter(u => u.status === 'pendente' || u.role === 'Solicitante');
            } else if (filterUserStatus === 'ativo') {
                list = list.filter(u => u.status !== 'pendente' && u.role !== 'Solicitante');
            }
        }

        // Filtro 5: Conexão
        if (filterUserConexao) {
            if (filterUserConexao === 'online') {
                list = list.filter(u => !!u.isOnline);
            } else if (filterUserConexao === 'offline') {
                list = list.filter(u => !u.isOnline);
            }
        }

        // Ordenação Padrão: Usuário em ordem alfabética (ou por coluna selecionada)
        list.sort((a, b) => {
            let cmp = 0;
            if (userSortField === 'name') {
                cmp = (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' });
            } else if (userSortField === 'department') {
                const deptA = depts?.find(d => String(d.id) === String(a.departmentId))?.name || a.department || '';
                const deptB = depts?.find(d => String(d.id) === String(b.departmentId))?.name || b.department || '';
                cmp = deptA.localeCompare(deptB, 'pt-BR', { sensitivity: 'base' });
            } else if (userSortField === 'role') {
                cmp = (a.role || '').localeCompare(b.role || '', 'pt-BR', { sensitivity: 'base' });
            } else if (userSortField === 'status') {
                const isPendingA = a.status === 'pendente' || a.role === 'Solicitante';
                const isPendingB = b.status === 'pendente' || b.role === 'Solicitante';
                const statusA = isPendingA ? 'Aguardando' : (a.status || 'Ativo');
                const statusB = isPendingB ? 'Aguardando' : (b.status || 'Ativo');
                cmp = statusA.localeCompare(statusB, 'pt-BR', { sensitivity: 'base' });
            } else if (userSortField === 'connection') {
                const connA = a.isOnline ? 'Online' : 'Offline';
                const connB = b.isOnline ? 'Online' : 'Offline';
                cmp = connA.localeCompare(connB, 'pt-BR', { sensitivity: 'base' });
            }

            // Desempate estável por nome
            if (cmp === 0 && userSortField !== 'name') {
                cmp = (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' });
            }

            return userSortDirection === 'asc' ? cmp : -cmp;
        });

        return list;
    }, [users, search, filterUserNome, filterUserDept, filterUserTipo, filterUserStatus, filterUserConexao, userSortField, userSortDirection, depts]);
    
    // Estados do Testador de API e Sincronização
    const [apiTestResult, setApiTestResult] = useState(null);
    const [isTestingApi, setIsTestingApi] = useState(false);
    const [syncModalData, setSyncModalData] = useState(null);
    const [selectedDetailsIntegration, setSelectedDetailsIntegration] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [jsonAvailableFields, setJsonAvailableFields] = useState([]);
    const [isLoadingFields, setIsLoadingFields] = useState(false);
    
    // Novos Estados para o Mapeamento Dinâmico
    const [firebaseFields, setFirebaseFields] = useState([]);
    const [isLoadingFbFields, setIsLoadingFbFields] = useState(false);
    
    // Estados de Modais
    const [isCadModalOpen, setIsCadModalOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isPermModalOpen, setIsPermModalOpen] = useState(false);
    
    const [form, setForm] = useState({});
    const [profileForm, setProfileForm] = useState({ name: '', permissions: {} });
    const [permissionForm, setPermissionForm] = useState({});
    const [editingUserName, setEditingUserName] = useState('');
   
    const [expandedPermGroups, setExpandedPermGroups] = useState({});

    // ESTADOS PARA O RATEIO DE CUSTOS POR DEPARTAMENTO (GESTOR)
    const [costSourceDeptId, setCostSourceDeptId] = useState(''); // depto de origem (do gestor)
    const [costTargetDeptId, setCostTargetDeptId] = useState(''); // depto de destino (centro de custo)
    const [costPercent, setCostPercent] = useState('');

    // Estado para feedback visual de upload das logomarcas
    const [uploadingLogoType, setUploadingLogoType] = useState(null);
    const [uploadStatusMsg, setUploadStatusMsg] = useState('');

    // Utilitário para redimensionar imagem antes do salvamento no Firestore (evita exceder 1MB)
    const resizeImageForUpload = (file, maxSize = 360) => {
        return new Promise((resolve, reject) => {
            if (!file) return resolve(null);
            
            const timeout = setTimeout(() => {
                reject(new Error("Tempo esgotado ao ler o arquivo de imagem."));
            }, 10000);

            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    clearTimeout(timeout);
                    try {
                        let width = img.naturalWidth || img.width;
                        let height = img.naturalHeight || img.height;

                        if (!width || !height) {
                            return resolve(event.target.result);
                        }

                        if (width > height) {
                            if (width > maxSize) {
                                height = Math.round((height * maxSize) / width);
                                width = maxSize;
                            }
                        } else {
                            if (height > maxSize) {
                                width = Math.round((width * maxSize) / height);
                                height = maxSize;
                            }
                        }

                        const canvas = document.createElement('canvas');
                        canvas.width = Math.max(1, width);
                        canvas.height = Math.max(1, height);

                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, width, height);

                        // Salva em PNG para preservar fundos transparentes
                        let dataUrl = canvas.toDataURL('image/png');

                        // Se por algum motivo o PNG ficar grande (> 250KB), reduz usando webp com boa qualidade
                        if (dataUrl.length > 250000) {
                            try {
                                const webpUrl = canvas.toDataURL('image/webp', 0.88);
                                if (webpUrl && webpUrl.startsWith('data:image/webp')) {
                                    dataUrl = webpUrl;
                                }
                            } catch (e) {}
                        }
                        resolve(dataUrl);
                    } catch (e) {
                        resolve(event.target.result);
                    }
                };
                img.onerror = () => {
                    clearTimeout(timeout);
                    resolve(event.target.result);
                };
                img.src = event.target.result;
            };
            reader.onerror = (err) => {
                clearTimeout(timeout);
                reject(err);
            };
            reader.readAsDataURL(file);
        });
    };

    // Upload de Imagem com redimensionamento proporcional seguro e salvamento em documento dedicado
    const handleLogoUpload = async (e, type) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingLogoType(type);
        setUploadStatusMsg('Otimizando imagem...');

        try {
            // max size: 500px para tela de login, 320px para logos da barra lateral
            const maxSize = type === 'login' ? 500 : 320;
            const resizedBase64 = await resizeImageForUpload(file, maxSize);
            if (!resizedBase64) {
                setUploadingLogoType(null);
                setUploadStatusMsg('');
                return;
            }

            setUploadStatusMsg('Salvando no banco...');

            const docIdMap = {
                sidebar_light: 'logo_sidebar_light',
                sidebar_dark: 'logo_sidebar_dark',
                sidebar_acad: 'logo_sidebar_acad',
                login: 'logo_login',
                sidebar: 'logo_sidebar'
            };

            const targetDocId = docIdMap[type] || `logo_${type}`;
            await saveSetting({ id: targetDocId, value: resizedBase64 });

            setUploadStatusMsg('Logo salvo com sucesso!');
            setTimeout(() => {
                setUploadStatusMsg('');
                setUploadingLogoType(null);
            }, 1200);
        } catch (error) {
            console.error("Erro ao salvar logomarca:", error);
            alert("A imagem selecionada não pôde ser salva: " + (error.message || "Erro desconhecido"));
            setUploadingLogoType(null);
            setUploadStatusMsg('');
        } finally {
            if (e.target) e.target.value = '';
        }
    };

    const handleRemoveLogo = async (type) => {
        const docIdMap = {
            sidebar_light: 'logo_sidebar_light',
            sidebar_dark: 'logo_sidebar_dark',
            sidebar_acad: 'logo_sidebar_acad',
            login: 'logo_login',
            sidebar: 'logo_sidebar'
        };

        const targetDocId = docIdMap[type] || `logo_${type}`;
        await saveSetting({ id: targetDocId, value: null });

        // Limpa também se existir no documento consolidado legado
        if (logos && logos[type]) {
            const cleanLegacy = { ...logos };
            delete cleanLegacy[type];
            delete cleanLegacy.id;
            await saveSetting({ id: 'logos', ...cleanLegacy });
        }
    };

    const handleAddCostCenter = () => {
        if (!costSourceDeptId || !costTargetDeptId || !costPercent) return;
        // Valida a soma APENAS para o grupo do sourceDeptId
        const groupTotal = (form.costCenters || [])
            .filter(cc => cc.sourceDeptId === costSourceDeptId)
            .reduce((acc, cc) => acc + Number(cc.percentage), 0);
        if (groupTotal + Number(costPercent) > 100) {
            const srcName = depts.find(d => String(d.id) === String(costSourceDeptId))?.name || costSourceDeptId;
            alert(`O rateio do departamento "${srcName}" não pode ultrapassar 100%.`);
            return;
        }
        const newEntry = { sourceDeptId: costSourceDeptId, targetDeptId: costTargetDeptId, percentage: Number(costPercent) };
        setForm({...form, costCenters: [...(form.costCenters || []), newEntry]});
        setCostTargetDeptId('');
        setCostPercent('');
    };

    const handleRemoveCostCenter = (index) => {
        const newCostCenters = [...form.costCenters];
        newCostCenters.splice(index, 1);
        setForm({...form, costCenters: newCostCenters});
    };

    // Auditoria Customizada (Novo Padrão)
    const logAction = (action, entity, recordId, detailsArray) => {
        const now = new Date();
        saveAuditLog({
            id: now.getTime().toString() + Math.random().toString(36).substring(2, 7),
            timestamp: now.getTime(),
            date: now.toISOString(),
            userId: currentUser?.email || currentUser?.id || 'Desconhecido',
            userName: currentUser?.name || 'Admin',
            userRole: currentUser?.role || 'SuperAdm',
            action: action,
            entity: entity,
            recordId: recordId || '',
            details: typeof detailsArray === 'string' ? detailsArray : JSON.stringify(detailsArray)
        });
    };

    // --- FUNÇÕES DE SALVAR ---
    const handleSave = () => {
        const isEdit = !!form.id; // Verifica se é edição
        const actionType = isEdit ? 'EDICAO' : 'CRIACAO';
        const item = { ...form, id: form.id || Date.now().toString() };
        
        // Array base de detalhes
        const detailsArr = [{ campo: "nome", para: item.name || item.title || "Registro Modificado" }];

        if (activeTab === 'dept') { saveDept(item); logAction(actionType, 'DEPARTAMENTOS', item.id, detailsArr); }
        if (activeTab === 'manager') { saveManager(item); logAction(actionType, 'GESTORES', item.id, detailsArr); }
        if (activeTab === 'users') { 
            saveUser({...item, role: item.role || 'ADM'}); 
            logAction(actionType, 'USUARIOS', item.id, detailsArr); 
        }
        if (activeTab === 'integrations') { 
            if (integrationsSubTab === 'softwares') {
                saveSoftware(item);
                logAction(actionType, 'SOFTWARES', item.id, detailsArr);
            } else {
                saveIntegration(item);
                logAction(actionType, 'INTEGRACOES', item.id, detailsArr); 
            }
        }
        
        if (activeTab === 'modules') { 
            const cleanModule = {
                id: item.id,
                title: item.title || 'Módulo sem nome',
                url: item.url || '',
                iconName: item.customIconUrl ? (item.iconName || '') : (item.iconName || 'LayoutGrid'),
                customIconUrl: item.customIconUrl || '',
                color: item.color || 'from-slate-500 to-slate-600',
                size: item.size || 'normal',
                moduleType: item.moduleType || 'interno',
                openMode: item.openMode || (item.openInNewTab ? 'newTab' : 'internal'),
                openInNewTab: item.openMode === 'newTab' || item.openInNewTab || false,
                isInternal: item.isInternal || false,
                order: item.order || 99,
                parentId: item.parentId || null
            };
            saveModule(cleanModule);
            logAction(actionType, 'MODULOS', cleanModule.id, [{ campo: "title", para: cleanModule.title }]);
        }
        
        setIsCadModalOpen(false);
        setForm({});
    };

    const handleLoadDefaultModules = () => {
        if (!window.confirm("Isso carregará os módulos originais para o banco de dados para edição. Continuar?")) return;
        MODULES.forEach(m => {
            saveModule({ ...m, iconName: m.iconName || 'LayoutGrid' });
        });
        logAction('CRIACAO', 'MODULOS', 'MULTIPLOS', [{ campo: "acao", para: "Importação Padrão do Sistema" }]);
        alert("Módulos carregados com sucesso!");
    };

    const handleDelete = (id, type) => {
        if (!window.confirm("Confirmar exclusão?")) return;
        
        const delDetails = [{ campo: "status", de: "Ativo", para: "Excluído" }];

        if (type === 'dept') { deleteDept(id); logAction('EXCLUSAO', 'DEPARTAMENTOS', id, delDetails); }
        if (type === 'manager') { deleteManager(id); logAction('EXCLUSAO', 'GESTORES', id, delDetails); }
        if (type === 'users') { deleteUser(id); logAction('EXCLUSAO', 'USUARIOS', id, delDetails); }
        if (type === 'integrations') { deleteIntegration(id); logAction('EXCLUSAO', 'INTEGRACOES', id, delDetails); } 
        if (type === 'softwares') { deleteSoftware(id); logAction('EXCLUSAO', 'SOFTWARES', id, delDetails); }
        if (type === 'modules') { 
            const isDefault = MODULES.find(m => m.id === id);
            if (isDefault) {
                saveModule({ id, _deleted: true });
            } else {
                deleteModule(id);
            }
            logAction('EXCLUSAO', 'MODULOS', id, delDetails);
        }
    };

    const handleDuplicateIntegration = (intToCopy) => {
        const { id, ...restOfData } = intToCopy; // Remove o ID antigo para não dar conflito
        const newIntegration = {
            ...restOfData,
            id: Date.now().toString(), // Gera um ID novinho
            name: `${restOfData.name} (Cópia)` // Avisa visualmente que é cópia
        };
        
        saveIntegration(newIntegration);
        logAction('CRIACAO', 'INTEGRACOES', newIntegration.id, [{ campo: "acao", para: `Duplicou a conexão: ${intToCopy.name}` }]);
    };

    const handleSaveProfile = () => {
        if(!profileForm.name) return alert("Dê um nome ao perfil.");
        const profileId = profileForm.id || Date.now().toString();
        
        savePermissionProfile({ ...profileForm, id: profileId });
        logAction('SALVAR_PERFIL', 'PERFIS', profileId, [{ campo: "perfil", para: profileForm.name }]);

        // Procura todos os usuários que estão vinculados a este perfil e atualiza eles
        let usersUpdated = 0;
        users.forEach(user => {
            if (user.profileId === profileId) {
                saveUser({ ...user, permissions: profileForm.permissions });
                usersUpdated++;
            }
        });

        setProfileForm({ name: '', permissions: {} });
        alert(`Perfil salvo com sucesso!\n\n${usersUpdated} usuário(s) vinculado(s) foram atualizados automaticamente com as novas permissões.`);
    };

    const handleSavePermissions = () => {
        const targetUser = users.find(u => u.id === permissionForm._userId);
        if (targetUser) {
            const { _userId, ...cleanPerms } = permissionForm;
            saveUser({ ...targetUser, permissions: cleanPerms });
            logAction('ALTERAR_PERMISSOES', 'USUARIOS', targetUser.id, [{ campo: "usuario", para: targetUser.name }]);
        }
        setIsPermModalOpen(false);
    };

    const handlePermissionChange = (targetKey, newValue, currentPerms, structure = permissionStructure) => {
        let updates = { ...currentPerms };
        updates[targetKey] = newValue;
        
        const cascadeDown = (items, keyToFind, forceValue) => {
            for (const item of items) {
                if (item.key === keyToFind) {
                    if (Array.isArray(item.linkedKeys)) {
                        item.linkedKeys.forEach(lk => { if (lk) updates[lk] = forceValue; });
                    }
                    if (item.children) {
                        const setAllChildren = (children) => {
                            children.forEach(child => {
                                if (child.key) updates[child.key] = forceValue;
                                if (Array.isArray(child.linkedKeys)) {
                                    child.linkedKeys.forEach(lk => { if (lk) updates[lk] = forceValue; });
                                }
                                if (child.children) setAllChildren(child.children);
                            });
                        };
                        setAllChildren(item.children);
                    }
                    return true;
                }
                if (item.children) if (cascadeDown(item.children, keyToFind, forceValue)) return true;
            }
            return false;
        };

        if (newValue === true) {
            const cascadeUp = (items, keyToFind, path = []) => {
                for (const item of items) {
                    const currentKeys = [item.key, ...(item.linkedKeys || [])].filter(Boolean);
                    if (item.key === keyToFind) {
                        path.forEach(parentKey => { if (parentKey) updates[parentKey] = true; });
                        currentKeys.forEach(k => { updates[k] = true; });
                        return true;
                    }
                    if (item.children) if (cascadeUp(item.children, keyToFind, [...path, ...currentKeys])) return true;
                }
                return false;
            };
            cascadeUp(structure, targetKey);
        }

        cascadeDown(structure, targetKey, newValue);

        // Mantém a chave agrupadora access_mod_05 sincronizada caso qualquer departamento (05.xx) esteja ativo
        const deptItems = (structure || []).filter(item => item.isDeptModule);
        if (deptItems.length > 0) {
            const anyDeptEnabled = deptItems.some(d => !!updates[d.key] || (Array.isArray(d.linkedKeys) && d.linkedKeys.some(lk => !!updates[lk])));
            updates['access_mod_05'] = anyDeptEnabled;
        }

        return updates;
    };

    const renderPermissionTree = (items, formState, setFormState, depth = 0) => {
        if (!items || items.length === 0) return null;
        return items.map((item) => {
            const isChecked = item.key ? !!formState[item.key] : false;
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedPermGroups[item.key || item.label];
            const paddingLeft = depth * 24 + 12; 

            return (
                <div key={item.key || item.label} className="flex flex-col">
                    <div className={`flex items-center gap-3 py-2 pr-2 border-b border-dashed transition-colors ${isDarkMode ? 'border-slate-800 hover:bg-slate-800/50' : 'border-gray-200 hover:bg-gray-50'} ${depth === 0 ? (isDarkMode ? 'bg-slate-900/50 font-bold mt-2 rounded-t' : 'bg-gray-100/80 font-bold mt-2 rounded-t text-gray-800') : ''}`} style={{ paddingLeft: `${paddingLeft}px` }}>
                        {hasChildren ? (
                            <button type="button" onClick={() => setExpandedPermGroups(prev => ({...prev, [item.key || item.label]: !prev[item.key || item.label]}))} className={`p-1 rounded ${isExpanded ? 'rotate-90' : ''}`}><ChevronRight size={14}/></button>
                        ) : <div className="w-6 h-6"></div>}

                        <label className="flex items-center gap-3 flex-1 cursor-pointer select-none">
                            {item.key && (
                                <input 
                                    type="checkbox" 
                                    checked={isChecked}
                                    onChange={(e) => {
                                        const newVal = e.target.checked;
                                        const newPermissions = handlePermissionChange(item.key, newVal, formState, permissionStructure);
                                        if (newVal && hasChildren) setExpandedPermGroups(prev => ({...prev, [item.key || item.label]: true}));
                                        setFormState(newPermissions);
                                    }}
                                    className="w-4 h-4 rounded text-blue-600"
                                />
                            )}
                            <span className={`text-sm ${isChecked ? 'text-blue-500 font-bold' : ''}`}>{item.label}</span>
                        </label>
                    </div>
                    {hasChildren && isExpanded && <div className="border-l ml-6 border-slate-700">{renderPermissionTree(item.children, formState, setFormState, depth + 1)}</div>}
                </div>
            );
        });
    };

    const filterList = (list) => list.filter(i => (i.name || i.title || '').toLowerCase().includes(search.toLowerCase()));

    const themeInput = `w-full p-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800'}`;
    const themeLabel = `text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`;

    return (
        <div className="flex flex-col h-full space-y-6 animate-fade-in pb-10">
            
           {/* ABAS DO ADM COM CONTROLE DE ACESSO */}
            <div className={`flex border-b shrink-0 bg-white dark:bg-slate-900 rounded-t-xl overflow-x-auto custom-scrollbar shadow-sm ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                {currentUser?.permissions?.access_adm_dept && (
                    <button onClick={() => { setActiveTab('dept'); setSearch(''); }} className={`shrink-0 px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'dept' ? 'border-blue-500 text-blue-500 bg-blue-50/10' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>Departamentos</button>
                )}
                
                {currentUser?.permissions?.access_adm_manager && (
                    <button onClick={() => { setActiveTab('manager'); setSearch(''); }} className={`shrink-0 px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'manager' ? 'border-blue-500 text-blue-500 bg-blue-50/10' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>Gestores</button>
                )}
                
                {currentUser?.permissions?.access_adm_users && (
                    <button onClick={() => { setActiveTab('users'); setSearch(''); }} className={`shrink-0 px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'users' ? 'border-blue-500 text-blue-500 bg-blue-50/10' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>Usuários e Acessos</button>
                )}
                
                {currentUser?.permissions?.access_adm_modules && (
                    <button onClick={() => { setActiveTab('modules'); setSearch(''); }} className={`shrink-0 px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'modules' ? 'border-blue-500 text-blue-500 bg-blue-50/10' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>Módulos do Sistema</button>
                )}

                {currentUser?.permissions?.access_adm_logos && (
                    <button onClick={() => { setActiveTab('logos'); setSearch(''); }} className={`shrink-0 px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'logos' ? 'border-blue-500 text-blue-500 bg-blue-50/10' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>Logomarca</button>
                )}
                {currentUser?.permissions?.access_adm_api && (
                    <button onClick={() => { setActiveTab('integrations'); setSearch(''); }} className={`shrink-0 px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'integrations' ? 'border-blue-500 text-blue-500 bg-blue-50/10' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>Integrações (APIs)</button>
                )}
            </div>

            {/* CONTEÚDO */}
            <div className={`rounded-xl shadow-md border flex-1 flex flex-col overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                
                {/* Header de Ações */}
                <div className={`p-4 flex flex-col sm:flex-row justify-between items-center gap-4 border-b shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                    {activeTab === 'users' ? (
                        <div className="flex gap-2">
                            <button 
                                onClick={() => setUsersSubTab('list')} 
                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 ${
                                    usersSubTab === 'list' 
                                        ? 'bg-[#1e40af] text-white ring-1 ring-[#1e40af]' 
                                        : (isDarkMode ? 'text-slate-300 hover:bg-slate-700 bg-slate-800/60' : 'text-slate-700 bg-white hover:bg-slate-100 border border-slate-300')
                                }`}
                                style={usersSubTab === 'list' ? { backgroundColor: '#1e40af', color: '#ffffff' } : {}}
                            >
                                <Users size={14} />
                                <span>Lista de Usuários</span>
                            </button>
                            <button 
                                onClick={() => setUsersSubTab('audit')} 
                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 ${
                                    usersSubTab === 'audit' 
                                        ? 'bg-[#6b21a8] text-white ring-1 ring-[#6b21a8]' 
                                        : (isDarkMode ? 'text-slate-300 hover:bg-slate-700 bg-slate-800/60' : 'text-slate-700 bg-white hover:bg-slate-100 border border-slate-300')
                                }`}
                                style={usersSubTab === 'audit' ? { backgroundColor: '#6b21a8', color: '#ffffff' } : {}}
                            >
                                <Shield size={14} />
                                <span>Auditoria</span>
                            </button>
                        </div>
                    ) : activeTab === 'integrations' ? (
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => setIntegrationsSubTab('list')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${integrationsSubTab === 'list' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}>Conexões API</button>
                            <button onClick={() => setIntegrationsSubTab('softwares')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${integrationsSubTab === 'softwares' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}>Softwares</button>
                            <button onClick={() => setIntegrationsSubTab('internal_team')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${integrationsSubTab === 'internal_team' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}>Equipe Interna (Acads)</button>
                            <button onClick={() => setIntegrationsSubTab('headcount')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${integrationsSubTab === 'headcount' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}>Headcount (apEx Clientes)</button>
                            <button onClick={() => setIntegrationsSubTab('logs')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${integrationsSubTab === 'logs' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}>Histórico & Monitoramento</button>
                        </div>
                    ) : (
                        <h3 className="font-bold text-lg">
                            {activeTab === 'dept' ? 'Gestão de Departamentos' : 
                             activeTab === 'manager' ? 'Gestão de Líderes' : 
                             activeTab === 'modules' ? 'Módulos do Sistema' : 
                             activeTab === 'integrations' ? 'Integrações e Webhooks' : 'Configurações de Logomarca'}
                        </h3>
                    )}

                    {!(activeTab === 'users' && usersSubTab === 'audit') && !(activeTab === 'integrations' && ['internal_team', 'headcount', 'logs'].includes(integrationsSubTab)) && (
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <div className="relative w-full sm:w-64">
                                <Search className={`absolute left-3 top-2.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} size={16} />
                                <Input placeholder="Pesquisar..." value={search} onChange={e=>setSearch(e.target.value)} className={`pl-9 h-9 ${isDarkMode ? 'bg-slate-900 border-slate-700' : ''}`}/>
                            </div>
                            
                            {activeTab === 'users' && usersSubTab === 'list' && (
                                <button 
                                    onClick={() => setShowUserFilters(!showUserFilters)} 
                                    className={`h-9 px-3.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2 border shadow-sm ${
                                        showUserFilters || activeUserFilterCount > 0
                                            ? (isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-500' : 'bg-[#0f6f7a] hover:bg-[#0a5963] text-white border-[#0f6f7a]')
                                            : (isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700' : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300')
                                    }`}
                                    title="Filtrar por Usuário, Departamento, Tipo, Status e Conexão"
                                >
                                    <Filter size={14} />
                                    <span>Filtrar</span>
                                    {activeUserFilterCount > 0 && (
                                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-black bg-amber-500 text-white leading-none">
                                            {activeUserFilterCount}
                                        </span>
                                    )}
                                </button>
                            )}

                            {activeTab === 'users' && (
                                <Button onClick={() => setIsProfileModalOpen(true)} className="h-9 text-xs bg-teal-600 hover:bg-teal-700 text-white border-0" icon={Shield}>Perfis</Button>
                            )}

                            <Button onClick={() => { setForm({}); setIsCadModalOpen(true); }} className="h-9 text-xs" icon={Plus}>Novo Registro</Button>
                        </div>
                    )}
                </div>

                {/* PAINEL DE FILTROS EXPANSÍVEL (USUÁRIOS) */}
                {activeTab === 'users' && usersSubTab === 'list' && showUserFilters && (
                    <div className={`p-4 border-b shrink-0 transition-all animate-fade-in ${isDarkMode ? 'bg-slate-900/90 border-slate-700' : 'bg-slate-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Filter size={15} className="text-blue-500" />
                                <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                    Filtros de Usuários
                                </span>
                                {activeUserFilterCount > 0 && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                                        {activeUserFilterCount} aplicado(s)
                                    </span>
                                )}
                            </div>
                            {activeUserFilterCount > 0 && (
                                <button 
                                    onClick={handleClearUserFilters}
                                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 font-semibold transition-colors"
                                >
                                    <X size={13} /> Limpar Filtros
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
                            {/* Usuário */}
                            <div>
                                <label className={themeLabel}>Usuário</label>
                                <div className="relative">
                                    <Search className={`absolute left-2.5 top-2.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} size={13} />
                                    <input 
                                        type="text" 
                                        placeholder="Filtrar por nome..." 
                                        value={filterUserNome} 
                                        onChange={e => setFilterUserNome(e.target.value)} 
                                        className={`w-full pl-8 pr-2.5 py-1.5 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                            isDarkMode ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800'
                                        }`}
                                    />
                                </div>
                            </div>

                            {/* Departamento */}
                            <div>
                                <label className={themeLabel}>Departamento</label>
                                <select 
                                    value={filterUserDept} 
                                    onChange={e => setFilterUserDept(e.target.value)} 
                                    className={`w-full p-1.5 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-800'
                                    }`}
                                >
                                    <option value="">Todos os Departamentos</option>
                                    {depts.map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Tipo */}
                            <div>
                                <label className={themeLabel}>Tipo</label>
                                <select 
                                    value={filterUserTipo} 
                                    onChange={e => setFilterUserTipo(e.target.value)} 
                                    className={`w-full p-1.5 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-800'
                                    }`}
                                >
                                    <option value="">Todos os Tipos</option>
                                    {availableUserRoles.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Status Acesso */}
                            <div>
                                <label className={themeLabel}>Status Acesso</label>
                                <select 
                                    value={filterUserStatus} 
                                    onChange={e => setFilterUserStatus(e.target.value)} 
                                    className={`w-full p-1.5 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-800'
                                    }`}
                                >
                                    <option value="">Todos os Status</option>
                                    <option value="ativo">Ativo</option>
                                    <option value="pendente">Aguardando (Pendente)</option>
                                </select>
                            </div>

                            {/* Conexão */}
                            <div>
                                <label className={themeLabel}>Conexão</label>
                                <select 
                                    value={filterUserConexao} 
                                    onChange={e => setFilterUserConexao(e.target.value)} 
                                    className={`w-full p-1.5 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-800'
                                    }`}
                                >
                                    <option value="">Todas</option>
                                    <option value="online">Online</option>
                                    <option value="offline">Offline</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabelas e Conteúdos */}
                <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50 dark:bg-slate-900/20">
                    
                    {/* LOGOMARCAS */}
                    {activeTab === 'logos' && (
                        <div className="p-6 space-y-6 max-w-4xl">
                            <div className="mb-2">
                                <h2 className="text-xl font-bold">Identidade Visual & Logomarcas</h2>
                                <p className="text-xs text-slate-500">Configure as logomarcas para a tela de login e para cada modo de exibição do cabeçalho da barra lateral.</p>
                            </div>

                            {/* LOGO LOGIN */}
                            <div className={`p-6 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="font-bold text-base">Logo da Tela de Login</h3>
                                        <p className="text-xs opacity-70">Exibida centralizada no topo da tela de acesso/login.</p>
                                    </div>
                                    {logos?.login && (
                                        <button 
                                            onClick={() => handleRemoveLogo('login')}
                                            className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 hover:bg-red-50 dark:border-red-900/40 dark:hover:bg-red-950/30 transition-colors"
                                        >
                                            <Trash2 size={13} /> Restaurar Padrão
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-col gap-4">
                                    <div className="p-4 bg-slate-950 rounded-lg inline-block self-start border border-slate-800">
                                        <img src={logos?.login || "/LogoApEx2.0.png"} className="h-16 object-contain" alt="Login Logo Preview" />
                                    </div>
                                    <div>
                                        <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, 'login')} className={`${themeInput} cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`} />
                                        {uploadingLogoType === 'login' ? (
                                            <div className="flex items-center gap-2 mt-2 text-xs font-bold text-blue-600 dark:text-blue-400 animate-pulse">
                                                <Loader2 size={14} className="animate-spin" />
                                                <span>{uploadStatusMsg}</span>
                                            </div>
                                        ) : (
                                            <p className="text-xs mt-2 opacity-70">Recomendado: Imagem retangular PNG com fundo transparente.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* LOGO MODO CLARO */}
                            <div className={`p-6 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="p-1.5 rounded-lg bg-amber-100 text-amber-700">
                                            <Sun size={18} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-base">Logo do Cabeçalho - Modo Claro</h3>
                                            <p className="text-xs opacity-70">Exibida no topo da Sidebar quando o usuário estiver no modo Claro.</p>
                                        </div>
                                    </div>
                                    {logos?.sidebar_light && (
                                        <button 
                                            onClick={() => handleRemoveLogo('sidebar_light')}
                                            className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 hover:bg-red-50 dark:border-red-900/40 dark:hover:bg-red-950/30 transition-colors"
                                        >
                                            <Trash2 size={13} /> Remover Logo
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-col gap-4">
                                    <div className="p-4 rounded-xl inline-block self-start border bg-[#fffdf8] border-[#dac9b5]">
                                        {logos?.sidebar_light ? (
                                            <img src={logos.sidebar_light} className="h-9 object-contain" alt="Logo Claro Preview" />
                                        ) : (
                                            <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                                                <div className="w-8 h-8 rounded-lg bg-[#077a68] text-white flex items-center justify-center font-bold">
                                                    M
                                                </div>
                                                <span>Ícone padrão ativo (nenhuma logo customizada)</span>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, 'sidebar_light')} className={`${themeInput} cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`} />
                                        {uploadingLogoType === 'sidebar_light' ? (
                                            <div className="flex items-center gap-2 mt-2 text-xs font-bold text-blue-600 dark:text-blue-400 animate-pulse">
                                                <Loader2 size={14} className="animate-spin" />
                                                <span>{uploadStatusMsg}</span>
                                            </div>
                                        ) : (
                                            <p className="text-xs mt-2 opacity-70">Recomendado: Logomarca otimizada para contraste em fundo claro.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* LOGO MODO ESCURO */}
                            <div className={`p-6 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="p-1.5 rounded-lg bg-slate-800 text-slate-200">
                                            <Moon size={18} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-base">Logo do Cabeçalho - Modo Escuro</h3>
                                            <p className="text-xs opacity-70">Exibida no topo da Sidebar quando o usuário estiver no modo Escuro.</p>
                                        </div>
                                    </div>
                                    {logos?.sidebar_dark && (
                                        <button 
                                            onClick={() => handleRemoveLogo('sidebar_dark')}
                                            className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 hover:bg-red-50 dark:border-red-900/40 dark:hover:bg-red-950/30 transition-colors"
                                        >
                                            <Trash2 size={13} /> Remover Logo
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-col gap-4">
                                    <div className="p-4 rounded-xl inline-block self-start border bg-slate-950 border-slate-800">
                                        {logos?.sidebar_dark ? (
                                            <img src={logos.sidebar_dark} className="h-9 object-contain" alt="Logo Escuro Preview" />
                                        ) : (
                                            <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
                                                <div className="w-8 h-8 rounded-lg bg-[#077a68] text-white flex items-center justify-center font-bold">
                                                    M
                                                </div>
                                                <span>Ícone padrão ativo (nenhuma logo customizada)</span>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, 'sidebar_dark')} className={`${themeInput} cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`} />
                                        {uploadingLogoType === 'sidebar_dark' ? (
                                            <div className="flex items-center gap-2 mt-2 text-xs font-bold text-blue-600 dark:text-blue-400 animate-pulse">
                                                <Loader2 size={14} className="animate-spin" />
                                                <span>{uploadStatusMsg}</span>
                                            </div>
                                        ) : (
                                            <p className="text-xs mt-2 opacity-70">Recomendado: Logomarca em tons claros/brancos com fundo transparente.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* LOGO MODO VERDE ACAD */}
                            <div className={`p-6 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="p-1.5 rounded-lg bg-emerald-900/60 text-emerald-300">
                                            <Leaf size={18} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-base">Logo do Cabeçalho - Modo Verde (Acad)</h3>
                                            <p className="text-xs opacity-70">Exibida no topo da Sidebar quando o usuário estiver no modo Verde.</p>
                                        </div>
                                    </div>
                                    {logos?.sidebar_acad && (
                                        <button 
                                            onClick={() => handleRemoveLogo('sidebar_acad')}
                                            className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 hover:bg-red-50 dark:border-red-900/40 dark:hover:bg-red-950/30 transition-colors"
                                        >
                                            <Trash2 size={13} /> Remover Logo
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-col gap-4">
                                    <div className="p-4 rounded-xl inline-block self-start border bg-[#062421] border-[#1b5e54]">
                                        {logos?.sidebar_acad ? (
                                            <img src={logos.sidebar_acad} className="h-9 object-contain" alt="Logo Verde Preview" />
                                        ) : (
                                            <div className="flex items-center gap-2 text-xs text-teal-300 font-medium">
                                                <div className="w-8 h-8 rounded-lg bg-[#077a68] text-white flex items-center justify-center font-bold">
                                                    M
                                                </div>
                                                <span>Ícone padrão ativo (nenhuma logo customizada)</span>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, 'sidebar_acad')} className={`${themeInput} cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`} />
                                        {uploadingLogoType === 'sidebar_acad' ? (
                                            <div className="flex items-center gap-2 mt-2 text-xs font-bold text-blue-600 dark:text-blue-400 animate-pulse">
                                                <Loader2 size={14} className="animate-spin" />
                                                <span>{uploadStatusMsg}</span>
                                            </div>
                                        ) : (
                                            <p className="text-xs mt-2 opacity-70">Recomendado: Logomarca com boa leitura sobre tons de verde escuro.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* LOGO GERAL / FALLBACK */}
                            <div className={`p-6 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="font-bold text-base">Logo Geral do Cabeçalho (Padrão de Reserva)</h3>
                                        <p className="text-xs opacity-70">Utilizada como padrão caso nenhum logo específico tenha sido cadastrado para o tema ativo.</p>
                                    </div>
                                    {logos?.sidebar && (
                                        <button 
                                            onClick={() => handleRemoveLogo('sidebar')}
                                            className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 hover:bg-red-50 dark:border-red-900/40 dark:hover:bg-red-950/30 transition-colors"
                                        >
                                            <Trash2 size={13} /> Remover Logo
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-col gap-4">
                                    <div className="p-4 bg-slate-950 rounded-lg inline-block self-start border border-slate-800">
                                        {logos?.sidebar ? (
                                            <img src={logos.sidebar} className="h-8 object-contain" alt="Sidebar Fallback Logo Preview" />
                                        ) : (
                                            <span className="text-xs text-slate-400">Nenhum logo de reserva definido.</span>
                                        )}
                                    </div>
                                    <div>
                                        <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, 'sidebar')} className={`${themeInput} cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`} />
                                        {uploadingLogoType === 'sidebar' ? (
                                            <div className="flex items-center gap-2 mt-2 text-xs font-bold text-blue-600 dark:text-blue-400 animate-pulse">
                                                <Loader2 size={14} className="animate-spin" />
                                                <span>{uploadStatusMsg}</span>
                                            </div>
                                        ) : (
                                            <p className="text-xs mt-2 opacity-70">Recomendado: Ícone quadrado ou marca reduzida.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* INTEGRAÇÕES DE API: SOFTWARES */}
                    {activeTab === 'integrations' && integrationsSubTab === 'softwares' && (
                        <div className="p-4 overflow-y-auto custom-scrollbar">
                            <table className="w-full text-sm text-left border rounded-lg overflow-hidden">
                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                    <tr><th className="p-4">Nome do Software</th><th className="p-4">Campos Exigidos</th><th className="p-4 text-center">Ações</th></tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                    {filterList(softwares).map(sw => (
                                        <tr key={sw.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                                            <td className="p-4 font-bold">{sw.name}</td>
                                            <td className="p-4 text-[10px] opacity-70">
                                                {[sw.useUrl && 'URL', sw.useMethod && 'Método', sw.useHeaders && 'Headers', sw.useDataPath && 'Caminho Array', sw.useBody && 'JSON Body', sw.useApiKey && 'API Key', sw.useModel && 'Modelo', sw.useActive && 'Ativo'].filter(Boolean).join(' | ')}
                                            </td>
                                            <td className="p-4 text-center flex justify-center gap-1">
                                                <button onClick={() => { setForm(sw); setIsCadModalOpen(true); }} className="text-blue-500 p-2 hover:bg-blue-55 rounded-lg"><Edit3 size={16}/></button>
                                                <button onClick={() => handleDelete(sw.id, 'softwares')} className="text-red-500 p-2 hover:bg-red-55 rounded-lg"><Trash2 size={16}/></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* INTEGRAÇÕES DE API: CONEXÕES */}
                    {activeTab === 'integrations' && integrationsSubTab === 'list' && (
                        <div className="p-4 overflow-y-auto custom-scrollbar">
                            <table className="w-full text-sm text-left border rounded-lg overflow-hidden">
                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                    <tr>
                                        <th className="p-4">Sistema / Integração</th>
                                        <th className="p-4">Tipo</th>
                                        <th className="p-4">Modelo</th>
                                        <th className="p-4">Ativo</th>
                                        <th className="p-4">Base URL (Endpoint)</th>
                                        <th className="p-4 text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                    {filterList(integrations).map(int => (
                                        <tr key={int.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                                            <td className="p-4 font-bold">{int.name}</td>
                                            <td className="p-4"><span className="px-2 py-1 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 uppercase">{int.type || 'API REST'}</span></td>
                                            <td className="p-4 font-mono text-xs opacity-70">{int.model || '-'}</td>
                                            <td className="p-4">
                                                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${int.isActive !== false ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'}`}>
                                                    {int.isActive !== false ? 'ATIVO' : 'INATIVO'}
                                                </span>
                                            </td>
                                            <td className="p-4 font-mono text-xs opacity-70 truncate max-w-[200px]">{int.baseUrl}</td>
                                            <td className="p-4 text-center flex justify-center gap-1">
                                                <button onClick={() => setSelectedDetailsIntegration(int)} className="text-indigo-600 dark:text-indigo-400 p-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg" title="Ver Detalhes e Documentação da Integração"><Info size={16}/></button>
                                                
                                                <button onClick={() => { setSyncModalData(int); setJsonAvailableFields([]); }} className="text-emerald-500 p-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg" title="Configurar Sincronização e Mapeamento"><Users size={16}/></button>
                                                
                                                <button onClick={() => handleDuplicateIntegration(int)} className="text-orange-500 p-2 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg" title="Duplicar Conexão API"><Copy size={16}/></button>
                                                
                                                <button onClick={() => { setForm(int); setApiTestResult(null); setIsCadModalOpen(true); }} className="text-blue-500 p-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg" title="Editar Conexão API"><Edit3 size={16}/></button>
                                                <button onClick={() => handleDelete(int.id, 'integrations')} className="text-red-500 p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg" title="Excluir"><Trash2 size={16}/></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* INTEGRAÇÕES DE API: EQUIPE INTERNA (ACADS) */}
                    {activeTab === 'integrations' && integrationsSubTab === 'internal_team' && (
                        <InternalTeamTab 
                            isDarkMode={isDarkMode} 
                            currentUser={currentUser} 
                            onNavigateToLogs={() => setIntegrationsSubTab('logs')} 
                        />
                    )}

                    {/* INTEGRAÇÕES DE API: HEADCOUNT (APEX CLIENTES) */}
                    {activeTab === 'integrations' && integrationsSubTab === 'headcount' && (
                        <HeadcountTab 
                            isDarkMode={isDarkMode} 
                            currentUser={currentUser} 
                            onNavigateToLogs={() => setIntegrationsSubTab('logs')} 
                        />
                    )}

                    {/* INTEGRAÇÕES DE API: HISTÓRICO & MONITORAMENTO */}
                    {activeTab === 'integrations' && integrationsSubTab === 'logs' && (
                        <IntegrationLogsTab 
                            logs={integrationLogs} 
                            isDarkMode={isDarkMode} 
                        />
                    )}

                    {/* MÓDULOS */}
                    {activeTab === 'modules' && (
                        <div className="p-4 overflow-y-auto custom-scrollbar">
                            {dbModules.length === 0 && (
                                <div className="mb-4 p-4 bg-blue-50 text-blue-800 rounded-lg flex justify-between items-center border border-blue-200 shrink-0">
                                    <p className="text-sm font-bold">Importe os módulos originais para o banco de dados caso queira editá-los.</p>
                                    <Button onClick={handleLoadDefaultModules} className="bg-blue-600 hover:bg-blue-700 text-white text-xs border-0">Importar Módulos Originais</Button>
                                </div>
                            )}

                            <table className="w-full text-sm text-left border rounded-lg overflow-hidden">
                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                    <tr>
                                        <th className="p-4">Nº</th>
                                        <th className="p-4">Módulo (Nome)</th>
                                        <th className="p-4 text-center">Tipo</th>
                                        <th className="p-4">Link Externo</th>
                                        <th className="p-4 text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                    {filterList(displayModules).sort((a,b) => String(a.id || '').localeCompare(String(b.id || ''), undefined, { numeric: true, sensitivity: 'base' })).map(m => (
                                        <tr key={m.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                                            <td className="p-4 font-bold font-mono text-blue-500">{m.id}</td>
                                            <td className="p-4 font-bold flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${m.color} flex items-center justify-center text-white`}>
                                                    {m.customIconUrl ? (
                                                        <img src={m.customIconUrl} className="w-4 h-4 object-contain" alt="icon"/>
                                                    ) : (
                                                        React.createElement(m.icon || LayoutGrid, { size: 16 })
                                                    )}
                                                </div>
                                                {m.title} {m.isInternal && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-slate-800 text-white rounded">NATIVO</span>}
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className={`px-2 py-1 rounded text-[10px] font-bold ${m.moduleType === 'externo' ? 'bg-purple-100 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:border-purple-800 dark:text-purple-400' : 'bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-400'}`}>
                                                    {m.moduleType === 'externo' ? 'EXTERNO' : 'INTERNO'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-xs opacity-70 truncate max-w-[200px]">{m.url || 'Nenhum'}</td>
                                            <td className="p-4 text-center">
                                                <button onClick={() => { setForm(m); setIsCadModalOpen(true); }} className="text-blue-500 p-2 hover:bg-blue-50 rounded-lg" title="Editar Módulo"><Edit3 size={16}/></button>
                                                <button onClick={() => handleDelete(m.id, 'modules')} className="text-red-500 p-2 hover:bg-red-50 rounded-lg" title="Excluir Módulo"><Trash2 size={16}/></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* DEPARTAMENTOS */}
                    {activeTab === 'dept' && (
                        <table className="w-full text-sm text-left">
                            <thead className={`text-xs uppercase font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                <tr>
                                    <th className="p-4">DEPARTAMENTO</th>
                                    <th className="p-4 text-center">STATUS</th>
                                    <th className="p-4">EMAIL</th>
                                    <th className="p-4">GESTOR(ES) VINCULADO(S)</th>
                                    <th className="p-4">RATEIO</th>
                                    <th className="p-4 text-center w-24">AÇÕES</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                {filterList(depts).map(d => {
                                    const linkedManagers = mgrs.map(m => {
                                        const mDeptIds = m.departmentIds?.length > 0
                                            ? m.departmentIds
                                            : (m.departmentId ? [m.departmentId] : []);
                                        
                                        const isLinked = mDeptIds.some(dId => String(dId) === String(d.id));
                                        if (!isLinked) return null;

                                        const sourceCCs = (m.costCenters || []).filter(cc => {
                                            const src = cc.sourceDeptId || (m.costCenters[0]?.deptId ? undefined : m.departmentId);
                                            return String(src || cc.deptId || '') === String(d.id);
                                        });
                                        const totalPct = sourceCCs.reduce((s, cc) => s + Number(cc.percentage), 0) || 100;
                                        return { id: m.id, name: m.name, percentage: totalPct };
                                    }).filter(Boolean);

                                    const isActive = !d.endDate;

                                    return (
                                        <tr key={d.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                                            <td className="p-4 font-bold">{d.name}</td>
                                            
                                            <td className="p-4 text-center">
                                                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${isActive ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'}`}>
                                                    {isActive ? 'ATIVO' : 'INATIVO'}
                                                </span>
                                            </td>

                                            <td className="p-4 opacity-80 italic text-xs">
                                                {d.email ? <span className="not-italic">{d.email}</span> : "Não informado"}
                                            </td>
                                            
                                            <td className="p-4">
                                                {linkedManagers.length > 0 ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        {linkedManagers.map(lm => (
                                                            <span key={`gestor-${lm.id}`} className="px-3 py-1 rounded text-xs font-bold border border-indigo-500/50 bg-indigo-900/40 text-indigo-300">
                                                                {lm.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : <span className="opacity-50 italic text-xs">Sem gestor vinculado</span>}
                                            </td>
                                            
                                            <td className="p-4">
                                                {linkedManagers.length > 0 ? (
                                                    <div className="flex flex-col gap-2">
                                                        {linkedManagers.map(lm => (
                                                            <div key={`rateio-${lm.id}`} className={`inline-flex w-fit items-center gap-2 px-3 py-1 rounded text-xs font-bold border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                                                <span>{lm.name}</span>
                                                                <span>-</span>
                                                                <span className="text-orange-500">{lm.percentage}%</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : <span className="opacity-50 italic text-xs">Sem rateio</span>}
                                            </td>
                                            
                                            <td className="p-4 text-center">
                                                <div className="flex justify-center gap-1">
                                                    <button onClick={() => { setForm(d); setIsCadModalOpen(true); }} className="text-blue-500 p-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"><Edit3 size={16}/></button>
                                                    <button onClick={() => handleDelete(d.id, 'dept')} className="text-red-500 p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"><Trash2 size={16}/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    {/* GESTORES */}
                    {activeTab === 'manager' && (
                        <table className="w-full text-sm text-left">
                            <thead className={`text-xs uppercase font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                <tr>
                                    <th className="p-4">NOME DO GESTOR</th>
                                    <th className="p-4 text-center">STATUS</th>
                                    <th className="p-4">EMAIL CORPORATIVO</th>
                                    <th className="p-4">DEPARTAMENTOS VINCULADOS</th>
                                    <th className="p-4">RATEIO DE CUSTOS</th>
                                    <th className="p-4 text-center">AÇÕES</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                {filterList(mgrs).map(m => {
                                    const linkedDeptIds = m.departmentIds?.length > 0
                                        ? m.departmentIds
                                        : (m.departmentId ? [m.departmentId] : []);

                                    const normalizedCostCenters = (m.costCenters || []).map(cc => ({
                                        sourceDeptId: cc.sourceDeptId || linkedDeptIds[0] || m.departmentId,
                                        targetDeptId: cc.targetDeptId || cc.deptId,
                                        percentage: cc.percentage
                                    }));

                                    const effectiveCostCenters = normalizedCostCenters.length > 0
                                        ? normalizedCostCenters
                                        : linkedDeptIds.map(dId => ({ sourceDeptId: dId, targetDeptId: dId, percentage: 100 }));

                                    const costBySource = {};
                                    effectiveCostCenters.forEach(cc => {
                                        if (!costBySource[cc.sourceDeptId]) costBySource[cc.sourceDeptId] = [];
                                        costBySource[cc.sourceDeptId].push(cc);
                                    });

                                    const isActive = !m.endDate;

                                    return (
                                        <tr key={m.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                                            <td className="p-4 font-bold">{m.name}</td>
                                            
                                            <td className="p-4 text-center">
                                                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${isActive ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'}`}>
                                                    {isActive ? 'ATIVO' : 'INATIVO'}
                                                </span>
                                            </td>

                                            <td className="p-4 opacity-80">{m.email}</td>
                                            <td className="p-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {linkedDeptIds.length > 0
                                                        ? linkedDeptIds.map(dId => (
                                                            <span key={dId} className="px-2 py-1 rounded text-[10px] font-bold border border-indigo-500/50 bg-indigo-900/20 text-indigo-400 dark:text-indigo-300">
                                                                {depts.find(d => String(d.id) === String(dId))?.name || dId}
                                                            </span>
                                                          ))
                                                        : <span className="opacity-50 italic text-xs">N/D</span>
                                                    }
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex flex-col gap-2">
                                                    {Object.entries(costBySource).map(([srcId, items]) => {
                                                        const srcName = depts.find(d => String(d.id) === String(srcId))?.name || srcId;
                                                        const total = items.reduce((s, cc) => s + Number(cc.percentage), 0);
                                                        return (
                                                            <div key={srcId} className={`rounded-lg border p-1.5 ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                                                <div className="flex items-center gap-1 mb-1">
                                                                    <span className="text-[9px] font-black uppercase opacity-60">De:</span>
                                                                    <span className="text-[10px] font-bold text-indigo-400">{srcName}</span>
                                                                    <span className={`ml-auto text-[9px] font-black px-1 rounded ${total === 100 ? 'bg-green-900/30 text-green-400' : 'bg-orange-900/30 text-orange-400'}`}>{total}%</span>
                                                                </div>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {items.map((cc, i) => (
                                                                        <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                                                            <span>{depts.find(d => String(d.id) === String(cc.targetDeptId))?.name || cc.targetDeptId}:</span>
                                                                            <span className="text-orange-500">{cc.percentage}%</span>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                            <td className="p-4 text-center">
                                                <div className="flex justify-center gap-1">
                                                    <button onClick={() => { 
                                                        const formData = { ...m };
                                                        if (!formData.departmentIds?.length) {
                                                            formData.departmentIds = formData.departmentId ? [formData.departmentId] : [];
                                                        }
                                                        if (formData.costCenters?.length && !formData.costCenters[0].sourceDeptId) {
                                                            const src = formData.departmentId || formData.departmentIds[0];
                                                            formData.costCenters = formData.costCenters.map(cc => ({
                                                                sourceDeptId: src,
                                                                targetDeptId: cc.deptId || cc.targetDeptId,
                                                                percentage: cc.percentage
                                                            }));
                                                        } else if (!formData.costCenters?.length) {
                                                            formData.costCenters = [];
                                                        }
                                                        setForm(formData);
                                                        setCostSourceDeptId(formData.departmentIds?.[0] || '');
                                                        setIsCadModalOpen(true); 
                                                    }} className="text-blue-500 p-2 hover:bg-blue-50 rounded-lg"><Edit3 size={16}/></button>
                                                    <button onClick={() => handleDelete(m.id, 'manager')} className="text-red-500 p-2 hover:bg-red-50 rounded-lg"><Trash2 size={16}/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    {/* USUÁRIOS */}
                    {activeTab === 'users' && usersSubTab === 'list' && (
                        <table className="w-full text-sm text-left">
                            <thead className={`text-xs uppercase font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                <tr>
                                    <th 
                                        className="p-4 cursor-pointer select-none hover:text-blue-600 transition-colors group"
                                        onClick={() => handleUserSort('name')}
                                        title="Classificar por Usuário"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <span>Usuário</span>
                                            {renderSortIcon('name')}
                                        </div>
                                    </th>
                                    <th className="p-4">Email</th>
                                    <th 
                                        className="p-4 cursor-pointer select-none hover:text-blue-600 transition-colors group"
                                        onClick={() => handleUserSort('department')}
                                        title="Classificar por Departamento"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <span>Departamento</span>
                                            {renderSortIcon('department')}
                                        </div>
                                    </th>
                                    <th 
                                        className="p-4 text-center cursor-pointer select-none hover:text-blue-600 transition-colors group"
                                        onClick={() => handleUserSort('role')}
                                        title="Classificar por Tipo"
                                    >
                                        <div className="flex items-center justify-center gap-1.5">
                                            <span>Tipo</span>
                                            {renderSortIcon('role')}
                                        </div>
                                    </th>
                                    <th 
                                        className="p-4 text-center cursor-pointer select-none hover:text-blue-600 transition-colors group"
                                        onClick={() => handleUserSort('status')}
                                        title="Classificar por Status de Acesso"
                                    >
                                        <div className="flex items-center justify-center gap-1.5">
                                            <span>Status Acesso</span>
                                            {renderSortIcon('status')}
                                        </div>
                                    </th>
                                    <th 
                                        className="p-4 text-center cursor-pointer select-none hover:text-blue-600 transition-colors group"
                                        onClick={() => handleUserSort('connection')}
                                        title="Classificar por Conexão"
                                    >
                                        <div className="flex items-center justify-center gap-1.5">
                                            <span>Conexão</span>
                                            {renderSortIcon('connection')}
                                        </div>
                                    </th>
                                    <th className="p-4 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                {filteredAndSortedUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="p-8 text-center text-slate-400">
                                            Nenhum usuário encontrado com os filtros aplicados.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredAndSortedUsers.map(u => {
                                        const isPending = u.status === 'pendente' || u.role === 'Solicitante';
                                        return (
                                            <tr key={u.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                                                <td className="p-4 font-bold flex items-center gap-2">
                                                    {u.name} 
                                                    {isPending && <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase bg-orange-200 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400">Pendente</span>}
                                                </td>
                                                <td className="p-4 opacity-80">{u.email}</td>
                                                <td className="p-4">{depts.find(d => String(d.id) === String(u.departmentId))?.name || '-'}</td>
                                                <td className="p-4 text-center">
                                                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${u.role === 'SuperAdm' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : u.role === 'Solicitante' ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>{u.role}</span>
                                                </td>
                                                <td className="p-4 text-center">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${isPending ? 'bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800' : (isDarkMode ? 'bg-green-900/30 text-green-400 border-green-800' : 'bg-green-50 text-green-700 border-green-200')}`}>
                                                        <CheckCircle size={10} />
                                                        {isPending ? 'Aguardando' : 'Ativo'}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-center">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${u.isOnline ? (isDarkMode ? 'bg-green-900/30 text-green-400 border-green-800' : 'bg-green-100 text-green-700 border-green-200') : (isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-500 border-gray-200')}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${u.isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                                                        {u.isOnline ? 'Online' : 'Offline'}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-center">
                                                    <div className="flex justify-center gap-1">
                                                        {isPending && (
                                                           <button onClick={() => { setForm({...u, role: 'ADM', status: 'ativo', isPending: false}); setIsCadModalOpen(true); }} className="text-green-600 p-2 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg" title="Aprovar Usuário"><CheckCircle size={16}/></button>
                                                        )}
                                                        <button onClick={() => { setForm(u); setIsCadModalOpen(true); }} className="text-blue-500 p-2 hover:bg-blue-55 rounded-lg" title="Editar Cadastro"><Edit3 size={16}/></button>
                                                        <button onClick={() => { setPermissionForm({ _userId: u.id, ...u.permissions }); setEditingUserName(u.name); setIsPermModalOpen(true); }} className="text-purple-500 p-2 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg" title="Permissões Específicas"><Shield size={16}/></button>
                                                        <button onClick={() => handleDelete(u.id, 'users')} className="text-red-500 p-2 hover:bg-red-50 rounded-lg"><Trash2 size={16}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}

                    {/* AUDITORIA */}
                    {activeTab === 'users' && usersSubTab === 'audit' && (
                        <AuditLogTab auditLogs={auditLogs} isDarkMode={isDarkMode} />
                    )}
                </div>
            </div>

            {/* MODAL DE CADASTRO GENÉRICO */}
            {isCadModalOpen && (
                <div className="absolute inset-0 z-[100] !mt-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsCadModalOpen(false)}>
                    <div className={`p-6 rounded-xl w-full max-w-lg shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6 border-b pb-3 dark:border-slate-700">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <Edit3 size={20} className="text-blue-500"/>
                                {form.id ? 'Editar Cadastro' : 'Novo Cadastro'}
                            </h3>
                            <button onClick={() => setIsCadModalOpen(false)}><X size={20}/></button>
                        </div>

                        <div className="space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
                            {activeTab === 'modules' ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className={themeLabel}>Nº de Ordem (Ex: 01, 10)</label>
                                            <input type="text" value={form.id || ''} onChange={e=>setForm({...form, id: e.target.value})} className={themeInput} placeholder="Posição" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className={themeLabel}>Nome do Módulo ou Submenu</label>
                                            <input type="text" value={form.title || ''} onChange={e=>setForm({...form, title: e.target.value})} className={themeInput} />
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={themeLabel}>Link de Acesso (URL iframe)</label>
                                            <input type="text" value={form.url || ''} onChange={e=>setForm({...form, url: e.target.value})} className={themeInput} placeholder="https://..." />
                                        </div>
                                        <div>
                                            <label className={themeLabel}>Módulo Pai (Para criar Submenu)</label>
                                            <select value={form.parentId || ''} onChange={e=>setForm({...form, parentId: e.target.value})} className={themeInput}>
                                                <option value="">Nenhum (Menu Principal)</option>
                                                {dbModules.filter(m => !m.parentId && m.id !== form.id).map(m => (
                                                    <option key={m.id} value={m.id}>{m.title}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                        <label className={themeLabel}>Ícone do Módulo</label>
                                        <div className="flex flex-wrap gap-4 mb-3">
                                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                <input type="radio" name="iconType" checked={form.iconSelectType !== 'custom' && !form.customIconUrl} onChange={() => setForm({...form, iconSelectType: 'default', customIconUrl: ''})} className="text-blue-500 accent-blue-500" /> 
                                                <span>Padrão do Sistema</span>
                                            </label>
                                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                <input type="radio" name="iconType" checked={form.iconSelectType === 'custom' || !!form.customIconUrl} onChange={() => setForm({...form, iconSelectType: 'custom', iconName: ''})} className="text-blue-500 accent-blue-500" /> 
                                                <span>Link ou Upload (Personalizado)</span>
                                            </label>
                                        </div>

                                        {(form.iconSelectType !== 'custom' && !form.customIconUrl) ? (
                                            <div className="flex items-center gap-3">
                                                <select value={form.iconName || ''} onChange={e=>setForm({...form, iconName: e.target.value})} className={`${themeInput} flex-1`}>
                                                    <option value="">Selecione um ícone padrão...</option>
                                                    {Object.keys(ICON_MAP).sort().map(k => <option key={k} value={k}>{k}</option>)}
                                                </select>
                                                
                                                <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 shadow-sm bg-gradient-to-br ${form.color || 'from-slate-500 to-slate-600'} text-white`}>
                                                    {React.createElement(ICON_MAP[form.iconName] || LayoutGrid, { size: 20 })}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <input 
                                                    type="text" 
                                                    value={form.customIconUrl || ''} 
                                                    onChange={e => {
                                                        let val = e.target.value;
                                                        const driveMatch = val.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || val.match(/id=([a-zA-Z0-9_-]+)/);
                                                        if (val.includes('drive.google.com') && driveMatch && driveMatch[1]) {
                                                            val = `https://lh3.googleusercontent.com/d/${driveMatch[1]}`;
                                                        }
                                                        setForm({...form, customIconUrl: val});
                                                    }} 
                                                    className={themeInput} 
                                                    placeholder="Link da imagem..." 
                                                />
                                                <label className="shrink-0 cursor-pointer bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300 px-4 py-2 rounded-lg text-sm font-bold flex items-center justify-center transition-colors">
                                                    Upload
                                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if(!file) return;
                                                        const reader = new FileReader();
                                                        reader.onloadend = () => {
                                                            const img = new Image();
                                                            img.onload = () => {
                                                                const canvas = document.createElement('canvas');
                                                                const MAX_SIZE = 128;
                                                                let width = img.width;
                                                                let height = img.height;
                                                                
                                                                if (width > height) {
                                                                    if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
                                                                } else {
                                                                    if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
                                                                }
                                                                
                                                                canvas.width = width;
                                                                canvas.height = height;
                                                                const ctx = canvas.getContext('2d');
                                                                ctx.drawImage(img, 0, 0, width, height);
                                                                
                                                                const resizedDataUrl = canvas.toDataURL('image/png');
                                                                setForm({...form, customIconUrl: resizedDataUrl, iconSelectType: 'custom'});
                                                            };
                                                            img.src = reader.result;
                                                        };
                                                        reader.readAsDataURL(file);
                                                    }} />
                                                </label>
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={themeLabel}>Cor do Card (Gradiente)</label>
                                            <select value={form.color || ''} onChange={e=>setForm({...form, color: e.target.value})} className={themeInput}>
                                                <option value="">Selecione uma cor...</option>
                                                {COLOR_PRESETS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={themeLabel}>Tamanho do Card (Tela Inicial)</label>
                                            <select value={form.size || 'normal'} onChange={e=>setForm({...form, size: e.target.value})} className={themeInput}>
                                                <option value="normal">Normal (1x1)</option>
                                                <option value="wide">Largo (2 Colunas)</option>
                                                <option value="tall">Alto (2 Linhas)</option>
                                                <option value="large">Grande (2x2 - Destaque)</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4 border-slate-200 dark:border-slate-700 items-end">
                                        <div>
                                            <label className={themeLabel}>Tipo de Exibição</label>
                                            <select value={form.moduleType || 'interno'} onChange={e=>setForm({...form, moduleType: e.target.value})} className={themeInput}>
                                                <option value="interno">Interno (Grade e Menu)</option>
                                                <option value="externo">Externo (Atalhos)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={themeLabel}>Comportamento de Abertura</label>
                                            <select value={form.openMode || (form.openInNewTab ? 'newTab' : 'internal')} onChange={e=>setForm({...form, openMode: e.target.value})} className={themeInput}>
                                                <option value="internal">Aba Interna do Hub</option>
                                                <option value="newTab">Nova Guia do Navegador</option>
                                                <option value="none">Apenas Agrupador (Não abre tela)</option>
                                            </select>
                                        </div>
                                        <div className="pb-2">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" checked={!!form.isInternal} onChange={e=>setForm({...form, isInternal: e.target.checked})} className="w-4 h-4 rounded text-blue-600 accent-blue-600" />
                                                <span className="text-sm font-bold leading-tight">Módulo Nativo<br/><span className="text-[10px] font-normal opacity-70">(Sem iframe)</span></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            ) : activeTab === 'manager' ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className={themeLabel}>Nome / Título</label>
                                            <input type="text" value={form.name || ''} onChange={e=>setForm({...form, name: e.target.value})} className={themeInput} />
                                        </div>
                                        <div>
                                            <label className={themeLabel}>ID Único do Sistema</label>
                                            <input type="text" value={form.id || 'Gerado automaticamente'} disabled className={`${themeInput} opacity-50 cursor-not-allowed`} />
                                        </div>
                                        <div>
                                            <label className={themeLabel}>Email Corporativo</label>
                                            <input type="email" value={form.email || ''} onChange={e=>setForm({...form, email: e.target.value})} className={themeInput} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className={themeLabel}>Imagem da Assinatura (Para PDFs)</label>
                                        <input type="file" className={`${themeInput} file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-slate-700 file:text-white cursor-pointer`} />
                                    </div>

                                    <div className={`border rounded-lg p-4 ${isDarkMode ? 'border-indigo-500/40 bg-slate-900/50' : 'border-indigo-200 bg-indigo-50/30'}`}>
                                        <label className={`${themeLabel} mb-3 text-indigo-500 dark:text-indigo-400`}>Departamentos Vinculados</label>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {depts.map(d => {
                                                const isChecked = (form.departmentIds || []).includes(String(d.id));
                                                return (
                                                    <label key={d.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                                                        isChecked
                                                            ? (isDarkMode ? 'border-indigo-500 bg-indigo-900/30 text-indigo-300 font-bold' : 'border-indigo-500 bg-indigo-100 text-indigo-700 font-bold')
                                                            : (isDarkMode ? 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300')
                                                    }`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={e => {
                                                                const current = (form.departmentIds || []);
                                                                const dId = String(d.id);
                                                                const updated = e.target.checked
                                                                    ? [...current, dId]
                                                                    : current.filter(x => x !== dId);
                                                                setForm({...form, departmentIds: updated});
                                                                if (e.target.checked && !costSourceDeptId) setCostSourceDeptId(dId);
                                                                if (!e.target.checked && costSourceDeptId === dId) {
                                                                    setCostSourceDeptId(updated[0] || '');
                                                                }
                                                            }}
                                                            className="w-4 h-4 rounded accent-indigo-600"
                                                        />
                                                        <span className="truncate">{d.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        {(form.departmentIds || []).length === 0 && (
                                            <p className="text-xs text-orange-500 mt-2 font-bold">⚠️ Selecione ao menos um departamento.</p>
                                        )}
                                    </div>

                                    {(form.departmentIds || []).length > 0 && (
                                    <div className={`border rounded-lg p-4 ${isDarkMode ? 'border-orange-500/50 bg-slate-900/50' : 'border-orange-300 bg-white'}`}>
                                        <label className={`${themeLabel} mb-3 text-orange-500`}>Rateio de Custos por Departamento</label>

                                        <div className="flex flex-wrap gap-1 mb-4">
                                            {(form.departmentIds || []).map(dId => {
                                                const groupTotal = (form.costCenters || [])
                                                    .filter(cc => cc.sourceDeptId === dId)
                                                    .reduce((s, cc) => s + Number(cc.percentage), 0);
                                                const isComplete = groupTotal === 100;
                                                return (
                                                    <button
                                                        key={dId}
                                                        type="button"
                                                        onClick={() => setCostSourceDeptId(dId)}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors flex items-center gap-1.5 ${
                                                            costSourceDeptId === dId
                                                                ? 'bg-orange-500 text-white border-orange-500'
                                                                : isDarkMode ? 'border-slate-700 bg-slate-800 text-slate-300 hover:border-orange-500/50' : 'border-gray-300 bg-gray-100 text-gray-700 hover:border-orange-400'
                                                        }`}
                                                    >
                                                        {depts.find(d => String(d.id) === String(dId))?.name || dId}
                                                        <span className={`text-[9px] font-black px-1 rounded ${isComplete ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-300'}`}>
                                                            {groupTotal}%
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {costSourceDeptId && (
                                            <>
                                                <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    Adicionando rateio para: <span className="text-orange-500">{depts.find(d => String(d.id) === String(costSourceDeptId))?.name || costSourceDeptId}</span>
                                                </div>
                                                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                                                    <select value={costTargetDeptId} onChange={e=>setCostTargetDeptId(e.target.value)} className={`${themeInput} flex-1`}>
                                                        <option value="">→ Departamento destino do custo</option>
                                                        {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                                    </select>
                                                    <div className="relative w-full sm:w-24">
                                                        <input type="number" min="1" max="100" placeholder="%" value={costPercent} onChange={e=>setCostPercent(e.target.value)} className={`${themeInput} w-full`} />
                                                    </div>
                                                    <Button onClick={handleAddCostCenter} className="bg-orange-500 hover:bg-orange-600 text-white border-0" type="button">Adicionar</Button>
                                                </div>
                                            
                                                <div className="space-y-2">
                                                    {(() => {
                                                        const groupItems = (form.costCenters || []).filter(cc => cc.sourceDeptId === costSourceDeptId);
                                                        const groupTotal = groupItems.reduce((s, cc) => s + Number(cc.percentage), 0);
                                                        return (
                                                            <>
                                                                {groupItems.map((cc, globalIdx) => {
                                                                    const realIdx = (form.costCenters || []).indexOf(cc);
                                                                    return (
                                                                        <div key={realIdx} className={`flex items-center justify-between gap-2 px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-300 bg-gray-50'}`}>
                                                                            <span className="font-bold flex-1">
                                                                                {depts.find(d => String(d.id) === String(cc.targetDeptId))?.name || cc.targetDeptId}
                                                                            </span>
                                                                            <span className="text-orange-500 font-bold">{cc.percentage}%</span>
                                                                            <button onClick={() => handleRemoveCostCenter(realIdx)} className="text-red-400 hover:text-red-500 ml-1" type="button"><X size={14}/></button>
                                                                        </div>
                                                                    );
                                                                })}
                                                                {groupItems.length === 0 && (
                                                                    <p className="text-xs italic opacity-50">Nenhum rateio definido para este departamento ainda.</p>
                                                                )}
                                                                <div className={`flex justify-between items-center px-3 py-1 rounded text-xs font-bold border ${groupTotal === 100 ? (isDarkMode ? 'border-green-800 bg-green-900/20 text-green-400' : 'border-green-300 bg-green-55 text-green-700') : (isDarkMode ? 'border-orange-800 bg-orange-900/20 text-orange-400' : 'border-orange-300 bg-orange-50 text-orange-700')}`}>
                                                                    <span>Total alocado neste depto:</span>
                                                                    <span>{groupTotal}% {groupTotal === 100 ? '✓ Completo' : `(faltam ${100 - groupTotal}%)`}</span>
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={themeLabel}>Início Vigência</label>
                                            <input type="date" value={form.startDate || ''} onChange={e=>setForm({...form, startDate: e.target.value})} className={themeInput} />
                                        </div>
                                        <div>
                                            <label className={themeLabel}>Fim Vigência</label>
                                            <input type="date" value={form.endDate || ''} onChange={e=>setForm({...form, endDate: e.target.value})} className={themeInput} />
                                        </div>
                                    </div>
                                </div>
                            ) : activeTab === 'integrations' ? (
                                <div className="space-y-4">
                                    {integrationsSubTab === 'softwares' ? (
                                        <>
                                            <div>
                                                <label className={themeLabel}>Nome do Software</label>
                                                <input type="text" value={form.name || ''} onChange={e=>setForm({...form, name: e.target.value})} className={themeInput} placeholder="Ex: Omie, Alterdata" />
                                            </div>
                                            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-gray-200'}`}>
                                                <label className={`${themeLabel} mb-3`}>Campos Obrigatórios</label>
                                                <div className="grid grid-cols-2 gap-3 text-sm">
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.useUrl} onChange={e=>setForm({...form, useUrl: e.target.checked})} className="rounded text-blue-500"/> URL Base</label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.useMethod} onChange={e=>setForm({...form, useMethod: e.target.checked})} className="rounded text-blue-500"/> Método</label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.useHeaders} onChange={e=>setForm({...form, useHeaders: e.target.checked})} className="rounded text-blue-500"/> Headers</label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.useDataPath} onChange={e=>setForm({...form, useDataPath: e.target.checked})} className="rounded text-blue-500"/> Caminho Array</label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.useBody} onChange={e=>setForm({...form, useBody: e.target.checked})} className="rounded text-blue-500"/> JSON Body</label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.useApiKey} onChange={e=>setForm({...form, useApiKey: e.target.checked})} className="rounded text-blue-500"/> API Key</label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.useModel} onChange={e=>setForm({...form, useModel: e.target.checked})} className="rounded text-blue-500"/> Modelo</label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.useActive} onChange={e=>setForm({...form, useActive: e.target.checked})} className="rounded text-blue-500"/> Ativo</label>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className={themeLabel}>Software Base</label>
                                                    <select value={form.softwareId || ''} onChange={e=>{
                                                        const sw = softwares.find(s => s.id === e.target.value);
                                                        setForm({...form, softwareId: e.target.value, name: sw ? `${sw.name} - ` : form.name});
                                                    }} className={themeInput}>
                                                        <option value="">Personalizado</option>
                                                        {softwares.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className={themeLabel}>Nome do Acesso / Integração</label>
                                                    <input type="text" value={form.name || ''} onChange={e=>setForm({...form, name: e.target.value})} className={themeInput} />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="md:col-span-2">
                                                    <label className={themeLabel}>URL Base (Endpoint)</label>
                                                    <input type="text" value={form.baseUrl || ''} onChange={e=>setForm({...form, baseUrl: e.target.value})} className={themeInput} placeholder="https://api..." />
                                                </div>
                                                <div>
                                                    <label className={themeLabel}>Método HTTP</label>
                                                    <select value={form.fetchMethod || 'GET'} onChange={e=>setForm({...form, fetchMethod: e.target.value})} className={themeInput}>
                                                        <option value="GET">GET</option>
                                                        <option value="POST">POST</option>
                                                        <option value="PUT">PUT</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div>
                                                <label className={themeLabel}>URL da Documentação Oficial da API</label>
                                                <input type="text" value={form.docsUrl || ''} onChange={e=>setForm({...form, docsUrl: e.target.value})} className={themeInput} placeholder="https://eplugin.pack.alterdata.com.br/#..." />
                                            </div>

                                            <div>
                                                <label className={themeLabel}>Como Funciona (Objetivo & Regras da Integração)</label>
                                                <textarea rows={3} value={form.howItWorks || ''} onChange={e=>setForm({...form, howItWorks: e.target.value})} className={`${themeInput} h-auto py-2`} placeholder="Explique aqui o propósito da integração, regras de negócio e parâmetros utilizados..." />
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : activeTab === 'dept' ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={themeLabel}>Nome / Título</label>
                                            <input type="text" value={form.name || ''} onChange={e=>setForm({...form, name: e.target.value})} className={themeInput} />
                                        </div>
                                        <div>
                                            <label className={themeLabel}>ID Único</label>
                                            <input type="text" value={form.id || 'Gerado automaticamente'} disabled className={`${themeInput} opacity-50 cursor-not-allowed`} />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className={themeLabel}>Email do Departamento</label>
                                            <input type="email" value={form.email || ''} onChange={e=>setForm({...form, email: e.target.value})} className={themeInput} placeholder="departamento@empresa.com" />
                                        </div>
                                        <div>
                                            <label className={themeLabel}>Início Vigência</label>
                                            <input type="date" value={form.startDate || ''} onChange={e=>setForm({...form, startDate: e.target.value})} className={themeInput} />
                                        </div>
                                        <div>
                                            <label className={themeLabel}>Fim Vigência</label>
                                            <input type="date" value={form.endDate || ''} onChange={e=>setForm({...form, endDate: e.target.value})} className={themeInput} />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div>
                                        <label className={themeLabel}>Nome Completo</label>
                                        <input type="text" value={form.name || ''} onChange={e=>setForm({...form, name: e.target.value})} className={themeInput} />
                                    </div>
                                    <div>
                                        <label className={themeLabel}>Email de Acesso</label>
                                        <input type="email" value={form.email || ''} onChange={e=>setForm({...form, email: e.target.value})} className={themeInput} />
                                    </div>
                                    {activeTab === 'users' && (
                                        <>
                                            <div>
                                                <label className={themeLabel}>Nível de Acesso</label>
                                                <select value={form.role || 'ADM'} onChange={e=>setForm({...form, role: e.target.value})} className={themeInput}>
                                                    <option value="ADM">Administrador Padrão</option>
                                                    <option value="SuperAdm">Super Admin</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className={themeLabel}>Perfil Base</label>
                                                <select value={form.profileId || ''} onChange={(e) => {
                                                    const prof = permissionProfiles.find(p => p.id === e.target.value);
                                                    if (prof) {
                                                        setForm(prev => ({ ...prev, profileId: prof.id, permissions: prof.permissions }));
                                                    } else {
                                                        setForm(prev => ({ ...prev, profileId: '' }));
                                                    }
                                                }} className={themeInput}>
                                                    <option value="">Acesso Personalizado</option>
                                                    {permissionProfiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className={themeLabel}>Departamento</label>
                                                <select value={form.departmentId || ''} onChange={e=>setForm({...form, departmentId: e.target.value})} className={themeInput}>
                                                    <option value="">Selecione...</option>
                                                    {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                                </select>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end gap-2 border-t pt-4 dark:border-slate-700">
                            <Button variant="secondary" onClick={() => setIsCadModalOpen(false)}>Cancelar</Button>
                            <Button onClick={handleSave} className="bg-blue-500 hover:bg-blue-600 text-white" icon={Save}>Salvar</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE PERMISSÕES INDIVIDUAIS */}
            {isPermModalOpen && (
                <div className="absolute inset-0 z-[100] !mt-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsPermModalOpen(false)}>
                    <div className={`rounded-xl w-full max-w-4xl shadow-2xl flex flex-col h-[85%] max-h-[85vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                        <div className={`p-6 flex justify-between items-center shrink-0 text-white ${isDarkMode ? 'bg-slate-900 border-b border-slate-700' : 'bg-slate-800'}`}>
                            <div>
                                <h3 className="font-bold text-xl flex items-center gap-2"><Shield size={24} className="text-blue-400"/> Gerenciar Permissões</h3>
                                <p className="text-sm opacity-80 mt-1">Usuário: <b className="text-blue-300">{editingUserName}</b></p>
                            </div>
                            <button onClick={() => setIsPermModalOpen(false)}><X size={24}/></button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50 dark:bg-slate-900/50">
                            {permissionStructure.map((project) => {
                                const isProjectEnabled = !!permissionForm[project.key] || (Array.isArray(project.linkedKeys) && project.linkedKeys.some(lk => !!permissionForm[lk]));
                                const isExpanded = expandedPermGroups[project.id] !== false;
                                const hasProjectChildren = Array.isArray(project.children) && project.children.length > 0;

                                return (
                                    <div key={project.id} className={`mb-6 border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-white'}`}>
                                        <div className={`p-4 flex items-center justify-between ${isProjectEnabled && isExpanded && hasProjectChildren ? 'border-b' : ''} ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                                            <label className="flex items-center gap-3 cursor-pointer">
                                                <input type="checkbox" checked={isProjectEnabled} onChange={e => {
                                                    const newVal = e.target.checked;
                                                    const newPerms = handlePermissionChange(project.key, newVal, permissionForm, permissionStructure);
                                                    if (newVal) setExpandedPermGroups(prev => ({...prev, [project.id]: true}));
                                                    setPermissionForm(newPerms);
                                                }} className="w-5 h-5 rounded text-blue-600" />
                                                <span className="font-bold text-lg">{project.label}</span>
                                            </label>
                                            {isProjectEnabled && hasProjectChildren && (
                                                <button onClick={() => setExpandedPermGroups(prev => ({...prev, [project.id]: !isExpanded}))} className="p-2 bg-black/5 dark:bg-white/10 rounded">
                                                    {isExpanded ? <Minus size={16}/> : <Plus size={16}/>}
                                                </button>
                                            )}
                                        </div>
                                        {isProjectEnabled && isExpanded && hasProjectChildren && (
                                            <div className="p-4">
                                                {renderPermissionTree(project.children, permissionForm, setPermissionForm)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={`p-5 border-t flex justify-end gap-3 shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                            <Button variant="secondary" onClick={() => setIsPermModalOpen(false)}>Cancelar</Button>
                            <Button onClick={handleSavePermissions} className="bg-blue-600 text-white">Salvar Acessos</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE PERFIS (PROFILE BUILDER) */}
            {isProfileModalOpen && (
                <div className="absolute inset-0 z-[100] !mt-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsProfileModalOpen(false)}>
                    <div className={`rounded-xl w-full max-w-6xl shadow-2xl flex h-[88%] max-h-[85vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                        
                        <div className={`w-1/3 max-w-xs shrink-0 border-r flex flex-col ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="p-4 border-b dark:border-slate-700 font-bold flex items-center gap-2">
                                <Shield size={20} className="text-teal-500"/> Perfis de Acesso
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 space-y-2">
                                {permissionProfiles.map(p => (
                                    <div key={p.id} className={`p-3 rounded-lg border flex justify-between items-center cursor-pointer transition-colors ${profileForm.id === p.id ? 'bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-300' : 'bg-white border-gray-200 dark:bg-slate-800 dark:border-slate-700 hover:border-teal-400'}`}>
                                        <span className="font-bold text-sm truncate flex-1" onClick={() => setProfileForm(p)}>{p.name}</span>
                                        <button onClick={() => { if(window.confirm('Excluir perfil?')) deletePermissionProfile(p.id) }} className="text-red-500 hover:bg-red-100 p-1.5 rounded"><Trash2 size={14}/></button>
                                    </div>
                                ))}
                                <Button onClick={() => setProfileForm({ name: '', permissions: {} })} className="w-full mt-4 bg-teal-600 text-white border-0" icon={Plus}>Novo Perfil</Button>
                            </div>
                        </div>

                        <div className="flex-1 min-w-0 flex flex-col">
                            <div className={`p-4 border-b flex gap-4 items-end ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                <div className="flex-1">
                                    <label className={themeLabel}>Nome do Perfil</label>
                                    <input type="text" value={profileForm.name} onChange={e=>setProfileForm({...profileForm, name: e.target.value})} className={themeInput} placeholder="Ex: Gerente" />
                                </div>
                                <Button onClick={handleSaveProfile} className="bg-teal-600 text-white h-10 shadow-md">Salvar Perfil</Button>
                                <Button variant="secondary" onClick={() => setIsProfileModalOpen(false)} className="h-10">Fechar</Button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50">
                                {permissionStructure.map((project) => {
                                    const isProjectEnabled = !!profileForm.permissions?.[project.key] || (Array.isArray(project.linkedKeys) && project.linkedKeys.some(lk => !!profileForm.permissions?.[lk]));
                                    const isExpanded = expandedPermGroups[project.id] !== false;
                                    const hasProjectChildren = Array.isArray(project.children) && project.children.length > 0;

                                    return (
                                        <div key={project.id} className={`mb-6 border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-white'}`}>
                                            <div className={`p-4 flex items-center justify-between ${isProjectEnabled && isExpanded && hasProjectChildren ? 'border-b' : ''} ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input type="checkbox" checked={isProjectEnabled} onChange={e => {
                                                        const newVal = e.target.checked;
                                                        const newPerms = handlePermissionChange(project.key, newVal, profileForm.permissions || {}, permissionStructure);
                                                        if (newVal) setExpandedPermGroups(prev => ({...prev, [project.id]: true}));
                                                        setProfileForm(prev => ({...prev, permissions: newPerms}));
                                                    }} className="w-5 h-5 rounded text-teal-600" />
                                                    <span className="font-bold text-lg">{project.label}</span>
                                                </label>
                                                {isProjectEnabled && hasProjectChildren && (
                                                    <button onClick={() => setExpandedPermGroups(prev => ({...prev, [project.id]: !isExpanded}))} className="p-2 bg-black/5 dark:bg-white/10 rounded">
                                                        {isExpanded ? <Minus size={16}/> : <Plus size={16}/>}
                                                    </button>
                                                )}
                                            </div>
                                            {isProjectEnabled && isExpanded && hasProjectChildren && (
                                                <div className="p-4">
                                                    {renderPermissionTree(project.children, profileForm.permissions || {}, (newPerms) => setProfileForm(prev => ({...prev, permissions: newPerms})))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE CONFIGURAÇÃO DE SINCRONIZAÇÃO E MAPEAMENTO DINÂMICO */}
            {syncModalData && (
                <div className="absolute inset-0 z-[100] !mt-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setSyncModalData(null)}>
                    <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                        
                        <div className="flex justify-between items-center mb-4 border-b pb-3 shrink-0 dark:border-slate-700">
                            <div>
                                <h3 className="font-bold text-lg flex items-center gap-2 text-emerald-500"><Users size={20}/> Mapeamento Dinâmico de Dados</h3>
                                <p className="text-xs opacity-70">Sincronizando via: {syncModalData.name}</p>
                            </div>
                            <button onClick={() => setSyncModalData(null)}><X size={20}/></button>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-6">
                            
                            {/* CONFIGURAÇÕES DE ALVO E REGRAS */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={themeLabel}>Tabela de Destino no Hub (Firebase)</label>
                                    <div className="flex gap-2">
                                        <input 
                                            type="text" 
                                            list="hubTablesList"
                                            value={syncModalData.targetTable || ''} 
                                            onChange={e => setSyncModalData({...syncModalData, targetTable: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})} 
                                            className={`${themeInput} flex-1`} 
                                            placeholder="Tabela de Destino..." 
                                        />
                                        <datalist id="hubTablesList">
                                            <option value="clients">Cadastro de Clientes</option>
                                            <option value="users">Sistema: Usuários</option>
                                            <option value="departments">Sistema: Departamentos</option>
                                            <option value="managers">Sistema: Gestores</option>
                                        </datalist>
                                        
                                        <button onClick={async () => {
                                            if(!syncModalData.targetTable) return console.warn("Digite o nome da tabela (Coleção) primeiro.");
                                            setIsLoadingFbFields(true);
                                            try {
                                                const fullPath = DB_PATH + syncModalData.targetTable;
                                                const q = query(collection(db, fullPath), limit(10));
                                                const snap = await getDocs(q);
                                                if (!snap.empty) {
                                                    const fields = new Set(['id']);
                                                    snap.docs.forEach(d => Object.keys(d.data()).forEach(k => fields.add(k)));
                                                    setFirebaseFields(Array.from(fields).sort());
                                                }
                                            } catch(e) { console.error(e); }
                                            setIsLoadingFbFields(false);
                                        }} className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 rounded-lg text-xs font-bold transition-colors whitespace-nowrap">
                                            {isLoadingFbFields ? <Loader2 className="animate-spin" size={14}/> : (firebaseFields.length > 0 ? '✅ Lidos!' : 'Ler Firebase')}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className={themeLabel}>Regra de Atualização</label>
                                    <select value={syncModalData.syncBehavior || 'upsert'} onChange={e => setSyncModalData({...syncModalData, syncBehavior: e.target.value})} className={themeInput}>
                                        <option value="upsert">Sobrescrever/Atualizar dados antigos</option>
                                        <option value="insert_only">Ignorar (Apenas incluir os novos)</option>
                                    </select>
                                </div>
                                
                                {(() => {
                                    let requiredVars = syncModalData.requestBody ? (syncModalData.requestBody.match(/\{\{([^}]+)\}\}/g) || []) : [];
                                    const visibleVars = requiredVars.filter(v => !v.includes('APP_KEY') && !v.includes('APP_SECRET'));
                                    if (visibleVars.length === 0) return null;
                                    return (
                                        <div className={`md:col-span-2 p-4 rounded-lg border border-dashed ${isDarkMode ? 'bg-indigo-900/20 border-indigo-500/50' : 'bg-indigo-50 border-indigo-300'}`}>
                                            <h4 className="font-bold text-sm text-indigo-500 mb-3 flex items-center gap-2"><Zap size={16}/> Variáveis Dinâmicas do Body</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {requiredVars.map(v => {
                                                    const varName = v.replace('{{', '').replace('}}', '');
                                                    return (
                                                        <div key={varName}>
                                                            <label className={themeLabel}>Preencha: {varName}</label>
                                                            <input type="text" value={syncVars[varName] || ''} onChange={e => setSyncVars({...syncVars, [varName]: e.target.value})} className={themeInput} placeholder={`Valor para ${varName}`} />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}
                                
                                <div className="md:col-span-2">
                                    <label className={themeLabel}>Caminho da Lista na API</label>
                                    <div className="flex gap-2">
                                        <input type="text" value={syncModalData.dataPath || ''} onChange={e=>setSyncModalData({...syncModalData, dataPath: e.target.value})} className={`${themeInput} flex-1`} placeholder="Ex: data" />
                                        
                                        <button onClick={async () => {
                                            setIsLoadingFields(true);
                                            try {
                                                const proxyUrl = "https://apiproxy-lqn64wkzdq-uc.a.run.app";
                                                const firstAcc = (syncModalData.hasMultipleAccounts && syncModalData.accounts?.length > 0) ? syncModalData.accounts[0] : null;
                                                const targetHeaders = { 'Content-Type': 'application/json' };
                                                if (syncModalData.headerKey) targetHeaders[syncModalData.headerKey] = firstAcc?.headerValue || syncModalData.headerValue || '';
                                                
                                                let finalBody = undefined;
                                                if (syncModalData.requestBody) {
                                                    let strBody = syncModalData.requestBody;
                                                    const vars = strBody.match(/\{\{([^}]+)\}\}/g) || [];
                                                    vars.forEach(v => { 
                                                        const cleanVar = v.replace('{{', '').replace('}}', '');
                                                        let val = syncVars[cleanVar] || '';
                                                        if (cleanVar === 'APP_KEY' && firstAcc?.appKey) val = firstAcc.appKey;
                                                        if (cleanVar === 'APP_SECRET' && firstAcc?.appSecret) val = firstAcc.appSecret;
                                                        strBody = strBody.replace(v, val); 
                                                    });
                                                    try { finalBody = JSON.parse(strBody); } catch(e) { throw new Error("JSON Body inválido."); }
                                                }
                                                const res = await fetch(proxyUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targetUrl: syncModalData.baseUrl, method: syncModalData.fetchMethod || 'GET', headers: targetHeaders, body: finalBody }) });
                                                const data = await res.json();
                                                
                                                if (data && data.faultstring) {
                                                    alert("Erro Omie: " + data.faultstring);
                                                    setIsLoadingFields(false); return;
                                                }

                                                let records = data;
                                                if (syncModalData.dataPath) {
                                                    const paths = syncModalData.dataPath.split('.');
                                                    paths.forEach(p => { if (records && records[p]) records = records[p]; });
                                                }

                                                if (!Array.isArray(records) || records.length === 0) {
                                                    alert("Nenhum registro encontrado.");
                                                } else {
                                                    setJsonAvailableFields(extractJsonPaths(records[0]));
                                                }
                                            } catch(e) { alert("Erro ao conectar: " + e.message); }
                                            setIsLoadingFields(false);
                                        }} disabled={isLoadingFields} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors shrink-0">
                                            {isLoadingFields ? <Loader2 className="animate-spin" size={16}/> : <Search size={16}/>} Ler Campos da API
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* VÍNCULO DE -> PARA */}
                            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-blue-50/50 border-blue-100'}`}>
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="font-bold text-sm text-indigo-500">Vincular Campos (De {'->'} Para)</h4>
                                    <button onClick={() => {
                                        const currentMap = syncModalData.dynamicMap || [];
                                        setSyncModalData({...syncModalData, dynamicMap: [...currentMap, { fbField: '', apiField: '' }]});
                                    }} className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400 px-3 py-1.5 rounded-lg font-bold transition-colors">
                                        + Novo Campo
                                    </button>
                                </div>

                                {jsonAvailableFields.length === 0 ? (
                                    <div className="text-center p-6 text-sm opacity-60 italic border-2 border-dashed rounded-lg border-indigo-200 dark:border-slate-700">
                                        Clique em <b>"Ler Campos da API"</b> acima para carregar a estrutura de origem.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex gap-2 px-2 text-[10px] uppercase tracking-wider font-bold opacity-70">
                                            <div className="flex-1">Destino (Seu Banco)</div>
                                            <div className="flex-1">Origem (API Externa)</div>
                                            <div className="w-8"></div>
                                        </div>

                                        <div className={`flex gap-2 items-center p-2 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                            <div className="flex-1">
                                                <input disabled value="id (Chave Primária)" className={`${themeInput} opacity-50 bg-gray-100 dark:bg-slate-800`} />
                                            </div>
                                            <div className="flex-1">
                                                <select value={syncModalData.dynamicMap?.find(m => m.fbField === 'id')?.apiField || ''} onChange={e => {
                                                    let map = [...(syncModalData.dynamicMap || [])];
                                                    const idx = map.findIndex(m => m.fbField === 'id');
                                                    if(idx >= 0) map[idx].apiField = e.target.value; else map.push({ fbField: 'id', apiField: e.target.value });
                                                    setSyncModalData({...syncModalData, dynamicMap: map});
                                                }} className={themeInput}>
                                                    <option value="">-- Selecione o ID da API --</option>
                                                    {jsonAvailableFields.map(f => <option key={f} value={f}>{f}</option>)}
                                                </select>
                                            </div>
                                            <div className="w-8"></div>
                                        </div>

                                        {(syncModalData.dynamicMap || []).filter(m => m.fbField !== 'id').map((mapItem, index) => (
                                            <div key={index} className={`flex gap-2 items-center p-2 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                                <div className="flex-1">
                                                    <input type="text" list="fbFieldsList" value={mapItem.fbField} onChange={e => {
                                                        const newMap = [...syncModalData.dynamicMap];
                                                        const idx = newMap.findIndex(m => m === mapItem);
                                                        newMap[idx].fbField = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                                                        setSyncModalData({...syncModalData, dynamicMap: newMap});
                                                    }} className={themeInput} placeholder="Ex: valor_total" />
                                                </div>
                                                <div className="flex-1">
                                                    <select value={mapItem.apiField} onChange={e => {
                                                        const newMap = [...syncModalData.dynamicMap];
                                                        const idx = newMap.findIndex(m => m === mapItem);
                                                        newMap[idx].apiField = e.target.value;
                                                        setSyncModalData({...syncModalData, dynamicMap: newMap});
                                                    }} className={themeInput}>
                                                        <option value="">-- Selecione a Origem --</option>
                                                        {jsonAvailableFields.map(f => <option key={f} value={f}>{f}</option>)}
                                                    </select>
                                                </div>
                                                <button onClick={() => {
                                                    const newMap = syncModalData.dynamicMap.filter(m => m !== mapItem);
                                                    setSyncModalData({...syncModalData, dynamicMap: newMap});
                                                }} className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg shrink-0"><Trash2 size={16}/></button>
                                            </div>
                                        ))}
                                        
                                        <datalist id="fbFieldsList">
                                            {firebaseFields.map(f => <option key={f} value={f} />)}
                                        </datalist>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* RODAPÉ */}
                        <div className="mt-4 shrink-0 flex flex-col md:flex-row justify-between items-center gap-3 border-t pt-4 dark:border-slate-700">
                            <button onClick={() => { saveIntegration(syncModalData); alert("Configurações Base Salvas!"); }} className="text-blue-500 hover:underline text-sm font-bold w-full md:w-auto text-left">
                                Salvar Configuração Base
                            </button>

                            <div className="flex gap-2 w-full md:w-auto">
                                <Button onClick={async () => {
                                        const idMap = (syncModalData.dynamicMap || []).find(m => m.fbField === 'id');
                                        if(!idMap || !idMap.apiField) return alert("Você precisa mapear o campo 'id (Chave Primária)'.");
                                        if(!syncModalData.targetTable) return alert("Você precisa definir a Tabela de Destino.");
                                        
                                        setIsSyncing(true);
                                        try {
                                            const proxyUrl = "https://apiproxy-lqn64wkzdq-uc.a.run.app";
                                            const firstAcc = (syncModalData.hasMultipleAccounts && syncModalData.accounts?.length > 0) ? syncModalData.accounts[0] : null;
                                            const targetHeaders = { 'Content-Type': 'application/json' };
                                            if (syncModalData.headerKey) targetHeaders[syncModalData.headerKey] = firstAcc?.headerValue || syncModalData.headerValue || '';
                                            
                                            let finalBody = undefined;
                                            if (syncModalData.requestBody) {
                                                let strBody = syncModalData.requestBody;
                                                const vars = strBody.match(/\{\{([^}]+)\}\}/g) || [];
                                                vars.forEach(v => { 
                                                    const cleanVar = v.replace('{{', '').replace('}}', '');
                                                    let val = syncVars[cleanVar] || '';
                                                    if (cleanVar === 'APP_KEY' && firstAcc?.appKey) val = firstAcc.appKey;
                                                    if (cleanVar === 'APP_SECRET' && firstAcc?.appSecret) val = firstAcc.appSecret;
                                                    strBody = strBody.replace(v, val); 
                                                });
                                                finalBody = JSON.parse(strBody);
                                            }
                                            
                                            const res = await fetch(proxyUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targetUrl: syncModalData.baseUrl, method: syncModalData.fetchMethod || 'GET', headers: targetHeaders, body: finalBody }) });
                                            const data = await res.json();
                                            
                                            if (data && data.faultstring) throw new Error(data.faultstring);

                                            let records = data;
                                            if (syncModalData.dataPath) {
                                                const paths = syncModalData.dataPath.split('.');
                                                paths.forEach(p => { if (records && records[p]) records = records[p]; });
                                            }

                                            if (!Array.isArray(records) || records.length === 0) {
                                                alert("Nenhum registro para teste.");
                                            } else {
                                                const item = records[0];
                                                const rawId = String(getNestedValue(item, idMap.apiField) || Date.now());
                                                const uniqueId = syncModalData.hasMultipleAccounts ? `${firstAcc?.appKey || 'acc'}_${rawId}`.replace(/[^a-zA-Z0-9_-]/g, '') : rawId;
                                                
                                                // Regra do tenant: usamos DB_PATH + tabela
                                                const targetPath = DB_PATH + syncModalData.targetTable;
                                                const docRef = doc(db, targetPath, uniqueId);
                                                
                                                let docExists = false;
                                                if (syncModalData.syncBehavior === 'insert_only') {
                                                    const docSnap = await getDoc(docRef);
                                                    docExists = docSnap.exists();
                                                }

                                                if (syncModalData.syncBehavior === 'insert_only' && docExists) {
                                                    alert("Teste ignorado: Este registro já existe.");
                                                } else {
                                                    let docData = {};
                                                    (syncModalData.dynamicMap || []).forEach(m => {
                                                        if(m.fbField && m.apiField && m.fbField !== 'id') docData[m.fbField] = getNestedValue(item, m.apiField);
                                                    });
                                                    docData.integrationSource = syncModalData.name;
                                                    docData.syncedAt = Date.now();

                                                    await setDoc(docRef, docData);
                                                    alert(`Teste OK! 1 registro importado no Firestore:\nID: ${uniqueId}`);
                                                }
                                            }
                                        } catch(e) { alert("Erro ao sincronizar: " + e.message); }
                                        setIsSyncing(false);
                                    }} disabled={isSyncing} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-lg transition-all flex items-center gap-2">
                                    {isSyncing ? <Loader2 className="animate-spin" size={16}/> : <Zap size={16}/>} Testar e Gravar 1 Registro
                                </Button>
                                <Button variant="secondary" onClick={() => setSyncModalData(null)}>Fechar</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* MODAL DE DETALHES DA INTEGRAÇÃO */}
            <IntegrationDetailsModal 
                integration={selectedDetailsIntegration} 
                onClose={() => setSelectedDetailsIntegration(null)} 
                isDarkMode={isDarkMode} 
            />

        </div>
    );
};

export default AdmModule;
